<template>
  <v-app class="esport-live-app">
    <div class="esport-live-page">
      <div v-if="REQUIRE_LOGIN && !isAuthed" class="login-gate">
        <form class="login-gate-box" @submit.prevent="handleLogin">
          <img src="/images/berkahbg.png" alt="logo" class="login-gate-logo" />

          <div class="login-game-badge">
            <v-icon small>mdi-gamepad-variant-outline</v-icon> PES LIVE SCORE
          </div>

          <h2>Akses Terbatas</h2>

          <p>Masukkan username &amp; password untuk melihat live score.</p>

          <input
            v-model="loginForm.username"
            type="text"
            placeholder="Username"
            autocomplete="username"
          />

          <input
            v-model="loginForm.password"
            type="password"
            placeholder="Password"
            autocomplete="current-password"
          />

          <button type="submit">Masuk</button>

          <span v-if="loginError" class="login-gate-error">{{ loginError }}</span>
        </form>
      </div>

      <template v-else>
        <header class="live-topbar">
          <div class="brand-area">
            <img src="/images/berkahbg.png" alt="Bondowoso Berkah" class="live-logo" />

            <div class="brand-text">
              <strong>Bondowoso Berkah</strong>
              <span class="brand-subtitle-desktop"
                >Diskominfo • Pro Evolution Soccer</span
              >
              <span class="brand-subtitle-mobile">PES Live Score</span>
            </div>

            <div class="game-divider"></div>

            <div class="game-title-wrap">
              <div class="game-icon">
                <v-icon color="#ffffff">mdi-gamepad-variant-outline</v-icon>
              </div>

              <div>
                <h1>PES TOURNAMENT</h1>
                <small>Citi Plaza - 2026</small>
              </div>
            </div>
          </div>

          <div class="header-actions">
            <div class="date-clock">
              <span
                ><v-icon x-small color="#ffffff">mdi-calendar-outline</v-icon>
                {{ currentDate }}</span
              >
              <span
                ><v-icon x-small color="#ffffff">mdi-clock-outline</v-icon>
                {{ currentTime }} WIB</span
              >
            </div>

            <div class="live-status" :class="{ offline: !isOnline }">
              <span class="live-status-dot"></span>
              <span class="status-full">{{
                isOnline ? "LIVE SCORE" : "MENGHUBUNGKAN"
              }}</span>
              <span class="status-short">{{ isOnline ? "LIVE" : "OFF" }}</span>
            </div>

            <button
              class="header-screenshot-btn"
              :disabled="capturingScreenshot"
              title="Ambil tangkapan layar bagan pertandingan secara utuh"
              @click="takeScreenshot"
            >
              <v-icon small color="#ffffff">mdi-camera-outline</v-icon>
              <span class="screenshot-btn-text">Screenshot</span>
            </button>

            <button class="icon-btn" @click="toggleFullscreen" title="Layar penuh">
              <v-icon small color="#ffffff">{{
                isFullscreen ? "mdi-fullscreen-exit" : "mdi-fullscreen"
              }}</v-icon>
            </button>
          </div>
        </header>

        <section
          v-if="bracket && (bracket.status === 'selesai' || bracket.juara_1_id)"
          class="champion-banner"
        >
          <div class="champion-title">
            <v-icon small>mdi-trophy-outline</v-icon><span>HASIL TURNAMEN</span>
          </div>

          <div class="champion-item c1">
            <v-icon small>mdi-trophy</v-icon><span>Juara 1</span
            ><strong>{{ namaPeserta(bracket.juara_1_id) }}</strong>
          </div>

          <div class="champion-item c2">
            <v-icon small>mdi-medal</v-icon><span>Juara 2</span
            ><strong>{{ namaPeserta(bracket.juara_2_id) }}</strong>
          </div>

          <div v-if="bracket.juara_3_id" class="champion-item c3">
            <v-icon small>mdi-medal-outline</v-icon><span>Juara 3</span
            ><strong>{{ namaPeserta(bracket.juara_3_id) }}</strong>
          </div>

          <div v-if="bracket.juara_4_id" class="champion-item c4">
            <v-icon small>mdi-medal-outline</v-icon><span>Juara 4</span
            ><strong>{{ namaPeserta(bracket.juara_4_id) }}</strong>
          </div>
        </section>

        <div v-if="!initialLoading && totalSlotUntukProyeksi < 2" class="live-empty">
          <div class="empty-icon"><v-icon size="42">mdi-tournament</v-icon></div>

          <h2>Menunggu Peserta</h2>

          <p>Bagan akan tampil otomatis begitu peserta terverifikasi mencukupi.</p>
        </div>

        <main v-else class="live-content">
          <div v-if="!bracket && !initialLoading" class="belum-generate-note">
            <v-icon small>mdi-information-outline</v-icon>

            <span
              >Turnamen belum dimulai — bagan sementara berdasarkan pendaftar
              terverifikasi.</span
            >
          </div>

          <div v-if="initialLoading" class="live-loading">
            <v-progress-circular indeterminate color="#028c73" size="34" />

            <span>Memuat bagan…</span>
          </div>

          <section
            v-else
            class="bracket-shell"
            :class="{ 'round-mode': selectedRound !== 'all' }"
          >
            <div class="round-navigation">
              <div class="round-navigation-head">
                <div class="round-navigation-title">
                  <span class="round-navigation-icon"
                    ><v-icon small>mdi-tournament</v-icon></span
                  >

                  <div>
                    <strong>NAVIGASI PERTANDINGAN</strong>

                    <small v-if="selectedRound === 'all'"
                      >Semua babak • Bracket lengkap</small
                    >

                    <small v-else
                      >{{ selectedRoundLabel }} •

                      {{ selectedRoundMatches.length }} pertandingan</small
                    >
                  </div>
                </div>

                <div class="round-search">
                  <v-icon x-small>mdi-magnify</v-icon>

                  <input
                    v-model="participantSearch"
                    type="search"
                    placeholder="Cari peserta..."
                    aria-label="Cari peserta"
                  />

                  <button
                    v-if="participantSearch"
                    type="button"
                    class="clear-search"
                    @click="participantSearch = ''"
                    title="Hapus pencarian"
                  >
                    <v-icon x-small>mdi-close</v-icon>
                  </button>
                </div>
              </div>

              <div class="round-tabs" role="tablist" aria-label="Pilih babak">
                <button
                  type="button"
                  class="round-tab all"
                  :class="{ active: selectedRound === 'all' }"
                  @click="selectRound('all')"
                >
                  <v-icon x-small>mdi-view-grid-outline</v-icon>

                  <span>SEMUA</span>
                </button>

                <button
                  v-for="round in roundNavigation"
                  :key="'nav-' + round.value"
                  type="button"
                  class="round-tab"
                  :class="{ active: selectedRound === round.value }"
                  @click="selectRound(round.value)"
                >
                  <span>{{ round.label }}</span>

                  <b>{{ round.count }}</b>
                </button>
              </div>
            </div>

            <div
              v-if="participantSearch && searchResults.length"
              class="search-result-banner"
            >
              <v-icon small>mdi-account-search-outline</v-icon>

              <span>
                Menemukan <strong>{{ searchResults.length }}</strong> pertandingan yang
                terkait dengan "<strong>{{ participantSearch }}</strong
                >"
              </span>

              <button type="button" @click="jumpToSearchResult(searchResults[0])">
                Lihat pertandingan

                <v-icon x-small>mdi-arrow-right</v-icon>
              </button>
            </div>

            <div
              v-else-if="participantSearch && !searchResults.length"
              class="search-result-banner empty"
            >
              <v-icon small>mdi-account-off-outline</v-icon>

              <span
                >Tidak menemukan peserta atau pertandingan dengan kata kunci "{{
                  participantSearch
                }}".</span
              >
            </div>

            <div v-if="selectedRound !== 'all'" class="round-view">
              <div class="round-view-heading">
                <div>
                  <span class="round-view-kicker">BABAK GUGUR</span>

                  <h2>{{ selectedRoundLabel }}</h2>
                </div>

                <div class="round-view-count">
                  <strong>{{ filteredSelectedRoundMatches.length }}</strong>

                  <span>pertandingan</span>
                </div>
              </div>

              <div v-if="filteredSelectedRoundMatches.length" class="round-match-grid">
                <div
                  v-for="(m, index) in filteredSelectedRoundMatches"
                  :key="'round-card-' + m.id"
                  class="round-match-item"
                  :class="{ 'search-highlight': isSearchMatch(m) }"
                  :data-round-match-id="m.id"
                >
                  <div class="round-match-meta">
                    <span>{{ selectedRoundLabel }}</span>
                  </div>

                  <EsportMatchCardView
                    :match="m"
                    :nama1="namaPeserta(m.peserta1_id)"
                    :nama2="namaPeserta(m.peserta2_id)"
                  />

                  <div v-if="m.jadwal_mulai" class="round-match-schedule">
                    <v-icon x-small>mdi-clock-outline</v-icon>

                    {{ formatSchedule(m.jadwal_mulai) }}
                  </div>
                </div>
              </div>

              <div v-else class="round-empty">
                <v-icon size="42">mdi-tournament-outline</v-icon>

                <strong>Belum ada pertandingan di babak ini</strong>

                <span>Pertandingan akan muncul jika data sudah tersedia.</span>
              </div>
            </div>

            <div
              v-else
              ref="bracketScroll"
              class="bracket-viewport"
              :class="{ 'hand-mode': handActive, 'is-panning': isPanning }"
              @mousedown="onPanMouseDown"
              @dragstart.capture="onPanDragStartCapture"
              @click.capture="onPanClickCapture"
            >
              <div class="bracket-quicknav">
                <div class="mode-pill-group" role="group" aria-label="Mode interaksi">
                  <button
                    type="button"
                    class="mode-pill-btn"
                    :class="{ active: !handActive }"
                    title="Mode Pilih: kursor biasa (tombol V)"
                    @click="setHandMode(false)"
                  >
                    <v-icon x-small>mdi-cursor-default-outline</v-icon>
                    <span>Kursor</span>
                    <kbd class="mode-kbd">V</kbd>
                  </button>

                  <button
                    type="button"
                    class="mode-pill-btn"
                    :class="{ active: handActive }"
                    title="Mode Tangan: tarik bagan untuk geser (tombol H, atau tahan Spasi)"
                    @click="setHandMode(true)"
                  >
                    <v-icon x-small>mdi-hand-back-right-outline</v-icon>
                    <span>Tangan</span>
                    <kbd class="mode-kbd">H</kbd>
                  </button>
                </div>

                <button
                  class="bqn-btn bqn-left"
                  @click="scrollToBracketSide('left')"
                  title="Lompat ke POL A"
                >
                  <v-icon x-small>mdi-chevron-double-left</v-icon>
                  <span>POL A</span>
                </button>
                <button
                  class="bqn-btn bqn-center"
                  @click="scrollToBracketSide('center')"
                  title="Lompat ke Final"
                >
                  <v-icon x-small>mdi-trophy-outline</v-icon>
                  <span>FINAL</span>
                </button>
                <button
                  class="bqn-btn bqn-right"
                  @click="scrollToBracketSide('right')"
                  title="Lompat ke POL B"
                >
                  <span>POL B</span>
                  <v-icon x-small>mdi-chevron-double-right</v-icon>
                </button>
              </div>

              <svg ref="bracketSvg" class="bracket-svg" aria-hidden="true"></svg>

              <div class="bracket-track">
                <div class="bracket-side-group bracket-side-group-left">
                  <div class="bracket-side-title">
                    <v-icon small>mdi-arrow-right-bold-circle-outline</v-icon
                    ><span>POL A</span>
                  </div>

                  <div class="bracket-half bracket-half-left">
                    <div
                      v-for="grp in sideRounds.left"
                      :key="'L-' + grp.ronde"
                      class="bracket-column"
                    >
                      <div class="bracket-column-header">
                        <div class="round-icon">
                          <v-icon small>mdi-format-list-numbered</v-icon>
                        </div>

                        <span>{{ grp.label }}</span>
                      </div>

                      <div class="bracket-column-body">
                        <EsportMatchCardView
                          v-for="m in grp.matches"
                          :key="m.id"
                          :match="m"
                          :nama1="namaPeserta(m.peserta1_id)"
                          :nama2="namaPeserta(m.peserta2_id)"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div class="bracket-center">
                  <div class="final-block">
                    <div class="final-label">
                      <span class="final-icon"
                        ><v-icon small>mdi-trophy-outline</v-icon></span
                      ><span>GRAND FINAL</span>
                    </div>

                    <div class="final-subtitle">BEST OF 2</div>

                    <div class="final-card-wrap">
                      <EsportMatchCardView
                        :match="finalDisplay"
                        :nama1="namaPeserta(finalDisplay.peserta1_id)"
                        :nama2="namaPeserta(finalDisplay.peserta2_id)"
                        variant="final"
                      />
                    </div>
                  </div>

                  <div v-if="juara3Display" class="j3-block">
                    <div class="j3-label">
                      <span class="j3-icon"
                        ><v-icon x-small>mdi-medal-outline</v-icon></span
                      ><span>PEREBUTAN JUARA 3</span>
                    </div>

                    <EsportMatchCardView
                      :match="juara3Display"
                      :nama1="namaPeserta(juara3Display.peserta1_id)"
                      :nama2="namaPeserta(juara3Display.peserta2_id)"
                      variant="small"
                    />
                  </div>
                </div>

                <div class="bracket-side-group bracket-side-group-right">
                  <div class="bracket-side-title">
                    <span>POL B</span
                    ><v-icon small>mdi-arrow-left-bold-circle-outline</v-icon>
                  </div>

                  <div class="bracket-half bracket-half-right">
                    <div
                      v-for="grp in sideRounds.right"
                      :key="'R-' + grp.ronde"
                      class="bracket-column"
                    >
                      <div class="bracket-column-header">
                        <span>{{ grp.label }}</span>

                        <div class="round-icon">
                          <v-icon small>mdi-format-list-numbered</v-icon>
                        </div>
                      </div>

                      <div class="bracket-column-body">
                        <EsportMatchCardView
                          v-for="m in grp.matches"
                          :key="m.id"
                          :match="m"
                          :nama1="namaPeserta(m.peserta1_id)"
                          :nama2="namaPeserta(m.peserta2_id)"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="handActive && selectedRound === 'all'" class="hand-mode-hint">
              <v-icon x-small color="#ffffff">{{
                isPanning ? "mdi-hand-back-right" : "mdi-hand-back-right-outline"
              }}</v-icon>
              <span>
                {{
                  spaceHeld
                    ? "Spasi ditahan — tarik bagan untuk geser"
                    : "Mode Tangan aktif — tarik bagan ke segala arah"
                }}
                <em>(V = Pilih · Tahan Spasi = tangan sementara)</em>
              </span>
            </div>
          </section>

          <section class="info-grid">
            <div class="info-card info-card-main">
              <div class="info-card-head">
                <span class="info-icon"
                  ><v-icon small>mdi-information-outline</v-icon></span
                ><strong>INFORMASI TURNAMEN</strong>
              </div>

              <div class="info-list">
                <div><span>Format</span><b>Single Elimination</b></div>

                <div><span>Game</span><b>Pro Evolution Soccer</b></div>

                <div>
                  <span>Peserta terverifikasi</span><b>{{ totalSlotUntukProyeksi }}</b>
                </div>
              </div>
            </div>

            <div class="info-card next-card">
              <div class="info-card-head">
                <span class="info-icon"
                  ><v-icon small>mdi-calendar-clock-outline</v-icon></span
                ><strong>PERTANDINGAN BERIKUTNYA</strong>
              </div>

              <div v-if="nextMatches.length" class="next-list">
                <div v-for="m in nextMatches" :key="'next-' + m.id" class="next-match">
                  <div>
                    <span class="next-round">{{ labelRonde(m.ronde) }}</span
                    ><b>{{ namaPeserta(m.peserta1_id) }}</b
                    ><span>vs</span><b>{{ namaPeserta(m.peserta2_id) }}</b>
                  </div>

                  <time>{{ formatSchedule(m.jadwal_mulai) }}</time>
                </div>
              </div>

              <div v-else class="next-empty">
                <v-icon small>mdi-check-circle-outline</v-icon>Belum ada pertandingan
                terjadwal.
              </div>
            </div>

            <div class="info-card quick-card">
              <div class="info-card-head">
                <span class="info-icon"><v-icon small>mdi-broadcast</v-icon></span
                ><strong>STATUS LIVE</strong>
              </div>

              <div class="live-summary">
                <div class="summary-number">{{ liveCount }}</div>

                <div>
                  <b>{{
                    liveCount
                      ? "Pertandingan sedang berlangsung"
                      : "Belum ada pertandingan live"
                  }}</b
                  ><span>dari total {{ matches.length }} pertandingan.</span>
                </div>
              </div>
            </div>
          </section>
        </main>

        <footer class="live-footer">
          <span
            ><v-icon x-small>mdi-gamepad-variant-outline</v-icon> E-Sports PES
            Tournament</span
          >

          <span>© {{ new Date().getFullYear() }} Bondowoso Berkah</span>

          <span>Diskominfo Bondowoso</span>
        </footer>
      </template>
    </div>

    <v-dialog
      v-model="screenshotDialogOpen"
      max-width="1240"
      scrollable
      content-class="screenshot-preview-dialog"
    >
      <v-card class="screenshot-dialog-card">
        <div class="screenshot-dialog-header">
          <div class="header-left">
            <div class="header-icon-box">
              <v-icon color="#028c73">mdi-image-filter-center-focus</v-icon>
            </div>
            <div class="header-title-text">
              <h3>Preview Bagan Pertandingan (Live)</h3>
              <p>
                Ukuran:
                <strong
                  >{{ screenshotDimensions.width }} ×
                  {{ screenshotDimensions.height }} px</strong
                >
                <span class="dot-separator">•</span>
                {{ screenshotDimensions.dateStr }}
              </p>
            </div>
            <button
              type="button"
              class="close-dialog-btn mobile-close-btn"
              title="Tutup Preview"
              @click="screenshotDialogOpen = false"
            >
              <v-icon small>mdi-close</v-icon>
            </button>
          </div>

          <div class="header-tools">
            <div class="zoom-pill-group">
              <button
                type="button"
                class="zoom-btn"
                title="Perkecil (-)"
                :disabled="zoomPercent <= 30"
                @click="zoomOut"
              >
                <v-icon small>mdi-minus</v-icon>
              </button>
              <span
                class="zoom-value"
                @click="resetZoom"
                title="Klik untuk sesuaikan ke lebar layar"
              >
                {{ zoomPercent }}%
              </span>
              <button
                type="button"
                class="zoom-btn"
                title="Perbesar (+)"
                :disabled="zoomPercent >= 500"
                @click="zoomIn"
              >
                <v-icon small>mdi-plus</v-icon>
              </button>
            </div>

            <button
              type="button"
              class="tool-btn"
              :class="{ active: zoomMode === 'fit' || zoomPercent === 100 }"
              title="Sesuaikan ukuran ke lebar layar modal"
              @click="setZoomMode('fit')"
            >
              <v-icon small>mdi-arrow-expand-horizontal</v-icon>
              <span>Pas Layar</span>
            </button>

            <button
              type="button"
              class="tool-btn"
              :class="{ active: zoomMode === 'actual' }"
              title="Tampilkan ukuran 100% asli"
              @click="setZoomMode('actual')"
            >
              <v-icon small>mdi-magnify-plus-outline</v-icon>
              <span>100% Asli</span>
            </button>

            <button
              type="button"
              class="close-dialog-btn desktop-close-btn"
              title="Tutup Preview"
              @click="screenshotDialogOpen = false"
            >
              <v-icon small>mdi-close</v-icon>
            </button>
          </div>
        </div>

        <v-card-text class="screenshot-dialog-body">
          <div class="screenshot-canvas-viewport">
            <div class="screenshot-img-container" :style="imageContainerStyle">
              <img
                v-if="screenshotImgUrl"
                :src="screenshotImgUrl"
                alt="Tangkapan Layar Bagan Live"
                class="screenshot-result-image"
              />
            </div>
          </div>
          <div class="viewport-hint">
            <v-icon x-small color="#94a3b8">mdi-information-outline</v-icon>
            <span>Tips: Gunakan tombol zoom di atas atau scroll untuk cek detail.</span>
          </div>
        </v-card-text>

        <v-card-actions class="screenshot-dialog-footer">
          <div class="footer-meta">
            <v-chip small color="#e8f6f2" text-color="#028c73" class="font-weight-bold">
              <v-icon left x-small color="#028c73">mdi-file-png-box</v-icon>
              PNG HD
            </v-chip>
            <span class="file-name-preview">Live-Bracket-PES-Bondowoso-Berkah.png</span>
          </div>

          <div class="footer-buttons">
            <div class="footer-secondary-btns">
              <v-btn outlined class="cancel-btn" @click="screenshotDialogOpen = false">
                <v-icon left small>mdi-close</v-icon>
                <span>Tutup</span>
              </v-btn>

              <v-btn
                outlined
                class="open-tab-btn"
                title="Buka gambar penuh di tab browser baru"
                @click="bukaDiTabBaru"
              >
                <v-icon left small>mdi-open-in-new</v-icon>
                <span>Tab Baru</span>
              </v-btn>
            </div>

            <v-btn
              depressed
              class="download-btn"
              title="Simpan file gambar PNG ke perangkat Anda"
              @click="downloadScreenshot"
            >
              <v-icon left small>mdi-download</v-icon>
              <span>Download Gambar (PNG)</span>
            </v-btn>
          </div>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-app>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, nextTick } from "vue";

import axios from "axios";
import html2canvas from "html2canvas";

import EsportMatchCardView from "./EsportMatchCardView.vue";

const REQUIRE_LOGIN = false;

const GATE_USERNAME = "pes";

const GATE_PASSWORD = "2026";

const GATE_SESSION_KEY = "pes_gate_ok";

const API_BASE = "https://berkah.bondowosokab.go.id:5500";

const ACCESS_KEY = "7039338ec2fa8ac649760ddd8404b711b207479377c846d41d5d24b7b21beb97";

const JENIS_LOMBA = "PRO EVOLUTION SOCCER";

const POLL_INTERVAL_MS = 5000;

const authHeaders = { headers: { "Bondowoso-Berkah": ACCESS_KEY } };

const capturingScreenshot = ref(false);
const screenshotDialogOpen = ref(false);
const screenshotImgUrl = ref("");
const screenshotDimensions = ref({ width: 0, height: 0, dateStr: "" });
const zoomPercent = ref(100);
const zoomMode = ref("fit");

const setZoomMode = (mode) => {
  zoomMode.value = mode;
  if (mode === "fit") {
    zoomPercent.value = 100;
  } else if (mode === "actual") {
    const targetW = screenshotDimensions.value.width
      ? Math.round(screenshotDimensions.value.width / 2)
      : 2400;
    const vp = document.querySelector(".screenshot-canvas-viewport");
    const vpWidth = vp ? Math.max(300, vp.clientWidth - 40) : 1100;
    zoomPercent.value = Math.max(100, Math.round((targetW / vpWidth) * 100));
  }
};

const zoomIn = () => {
  zoomMode.value = "custom";
  zoomPercent.value = Math.min(500, zoomPercent.value + 25);
};

const zoomOut = () => {
  zoomMode.value = "custom";
  zoomPercent.value = Math.max(30, zoomPercent.value - 25);
};

const resetZoom = () => {
  setZoomMode("fit");
};

const imageContainerStyle = computed(() => {
  if (zoomMode.value === "fit" || zoomPercent.value === 100) {
    return {
      width: "100%",
      maxWidth: "100%",
      minWidth: "100%",
      margin: "0 auto",
      display: "block",
    };
  }
  return {
    width: `${zoomPercent.value}%`,
    maxWidth: "none",
    minWidth: `${zoomPercent.value}%`,
    margin: "0 auto",
    display: "block",
  };
});

const takeScreenshot = async () => {
  capturingScreenshot.value = true;

  const prevSelectedRound = selectedRound.value;
  if (selectedRound.value !== "all") {
    selectedRound.value = "all";
    await nextTick();
    await new Promise((r) => setTimeout(r, 150));
  }

  const container = bracketScroll.value;
  if (!container) {
    alert("Papan bagan pertandingan belum siap.");
    capturingScreenshot.value = false;
    selectedRound.value = prevSelectedRound;
    return;
  }

  const savedScrollLeft = container.scrollLeft;
  const savedScrollTop = container.scrollTop;

  try {
    if (document.fonts && document.fonts.ready) {
      await document.fonts.ready;
    }

    container.scrollLeft = 0;
    container.scrollTop = 0;

    await nextTick();
    drawBracketLines();
    await new Promise((resolve) => setTimeout(resolve, 200));

    const fullWidth = container.scrollWidth;
    const fullHeight = container.scrollHeight;

    const bgImg = await new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => resolve(null);
      img.src = "/images/bglivepes.png";
    });

    const canvas = await html2canvas(container, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      backgroundColor: null,
      width: fullWidth,
      height: fullHeight,
      scrollX: 0,
      scrollY: 0,
      onclone: (clonedDoc) => {
        const clonedScroll = clonedDoc.querySelector(".bracket-viewport");
        if (clonedScroll) {
          clonedScroll.style.overflow = "visible";
          clonedScroll.style.width = fullWidth + "px";
          clonedScroll.style.height = fullHeight + "px";
          clonedScroll.style.maxWidth = "none";
          clonedScroll.style.maxHeight = "none";
          clonedScroll.style.scrollLeft = 0;
          clonedScroll.scrollTop = 0;
          clonedScroll.style.background = "transparent";

          const clonedSvg = clonedScroll.querySelector(".bracket-svg");
          if (clonedSvg) {
            drawBracketLines(clonedScroll, clonedSvg);
          }
        }
      },
      ignoreElements: (element) => {
        return element.classList && element.classList.contains("bracket-quicknav");
      },
    });

    const scaleFactor = Math.max(1, canvas.width / 2000);
    const headerHeight = Math.round(110 * scaleFactor);
    const footerHeight = Math.round(40 * scaleFactor);
    const padX = Math.round(40 * scaleFactor);

    const finalCanvas = document.createElement("canvas");
    finalCanvas.width = canvas.width;
    finalCanvas.height = canvas.height + headerHeight + footerHeight;

    const ctx = finalCanvas.getContext("2d");

    const bgGradient = ctx.createLinearGradient(
      0,
      0,
      finalCanvas.width,
      finalCanvas.height
    );
    bgGradient.addColorStop(0, "#e8f5f1");
    bgGradient.addColorStop(0.5, "#f4fbf8");
    bgGradient.addColorStop(1, "#e2f4ef");
    ctx.fillStyle = bgGradient;
    ctx.fillRect(0, 0, finalCanvas.width, finalCanvas.height);

    if (bgImg && bgImg.complete && bgImg.naturalWidth > 0) {
      ctx.drawImage(bgImg, 0, headerHeight, finalCanvas.width, canvas.height);
      ctx.fillStyle = "rgba(236, 247, 244, 0.78)";
      ctx.fillRect(0, headerHeight, finalCanvas.width, canvas.height);
    }

    const gradient = ctx.createLinearGradient(0, 0, finalCanvas.width, 0);
    gradient.addColorStop(0, "#028c73");
    gradient.addColorStop(1, "#016653");
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, finalCanvas.width, headerHeight);

    const goldBarHeight = Math.max(4, Math.round(4 * scaleFactor));
    ctx.fillStyle = "#b77d16";
    ctx.fillRect(0, headerHeight - goldBarHeight, finalCanvas.width, goldBarHeight);

    const titleSize = Math.round(28 * scaleFactor);
    const subSize = Math.round(15 * scaleFactor);
    const statusSize = Math.round(16 * scaleFactor);
    const timeSize = Math.round(13 * scaleFactor);
    const footerSize = Math.round(12 * scaleFactor);

    ctx.fillStyle = "#ffffff";
    ctx.font = `bold ${titleSize}px sans-serif`;
    ctx.fillText(
      "TURNAMEN PRO EVOLUTION SOCCER (PES)",
      padX,
      Math.round(headerHeight * 0.44)
    );

    ctx.fillStyle = "#e8f6f2";
    ctx.font = `600 ${subSize}px sans-serif`;
    const totalPesertaStr = peserta.value.length
      ? `${peserta.value.length} Slot Peserta`
      : `${totalSlotUntukProyeksi.value} Peserta Terdaftar`;
    ctx.fillText(
      `BONDOWOSO BERKAH • LIVE BRACKET PERTANDINGAN `,
      padX,
      Math.round(headerHeight * 0.78)
    );

    const waktuCetak = new Date().toLocaleString("id-ID", {
      dateStyle: "medium",
      timeStyle: "short",
    });
    ctx.textAlign = "right";
    ctx.fillStyle = "#fef08a";
    ctx.font = `bold ${statusSize}px sans-serif`;
    const statusText = `STATUS: ${(bracket.value?.status || "AKTIF").toUpperCase()}`;
    ctx.fillText(statusText, finalCanvas.width - padX, Math.round(headerHeight * 0.44));

    ctx.fillStyle = "#ccfbf1";
    ctx.font = `500 ${timeSize}px sans-serif`;
    ctx.fillText(
      `Tangkapan Layar: ${waktuCetak}`,
      finalCanvas.width - padX,
      Math.round(headerHeight * 0.76)
    );

    ctx.textAlign = "left";

    ctx.drawImage(canvas, 0, headerHeight);

    ctx.fillStyle = "#f1f5f9";
    ctx.fillRect(0, finalCanvas.height - footerHeight, finalCanvas.width, footerHeight);
    ctx.fillStyle = "#cbd5e1";
    ctx.fillRect(0, finalCanvas.height - footerHeight, finalCanvas.width, 1);

    ctx.textAlign = "center";
    ctx.fillStyle = "#64748b";
    ctx.font = `500 ${footerSize}px sans-serif`;
    ctx.fillText(
      "© Diskominfo Kabupaten Bondowoso • Live Score Turnamen E-Sport Bondowoso Berkah",
      finalCanvas.width / 2,
      finalCanvas.height - Math.round(footerHeight * 0.35)
    );

    screenshotImgUrl.value = finalCanvas.toDataURL("image/png");
    screenshotDimensions.value = {
      width: finalCanvas.width,
      height: finalCanvas.height,
      dateStr: waktuCetak,
    };
    setZoomMode("fit");
    screenshotDialogOpen.value = true;
  } catch (err) {
    console.error("Gagal mengambil screenshot bagan client:", err);
    alert("Gagal mengambil screenshot bagan. Silakan coba kembali.");
  } finally {
    container.scrollLeft = savedScrollLeft;
    container.scrollTop = savedScrollTop;
    if (selectedRound.value !== prevSelectedRound) {
      selectedRound.value = prevSelectedRound;
    } else {
      requestAnimationFrame(drawBracketLines);
    }
    capturingScreenshot.value = false;
  }
};

const downloadScreenshot = () => {
  if (!screenshotImgUrl.value) return;
  const link = document.createElement("a");
  const now = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  const dateStr = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
  const timeStr = `${pad(now.getHours())}.${pad(now.getMinutes())}.${pad(
    now.getSeconds()
  )}`;
  link.download = `Live-Bracket-PES-Bondowoso-Berkah-${dateStr}_${timeStr}.png`;
  link.href = screenshotImgUrl.value;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const bukaDiTabBaru = () => {
  if (!screenshotImgUrl.value) return;
  const w = window.open("");
  if (w) {
    w.document.write(`
      <html>
        <head>
          <title>Live Bracket PES - Bondowoso Berkah</title>
          <style>
            body {
              margin: 0;
              padding: 24px;
              background: #0f172a;
              display: flex;
              flex-direction: column;
              align-items: center;
              font-family: sans-serif;
            }
            img {
              max-width: 100%;
              height: auto;
              border-radius: 10px;
              box-shadow: 0 15px 40px rgba(0,0,0,0.6);
            }
            .info {
              color: #94a3b8;
              font-size: 13px;
              margin-bottom: 16px;
            }
          </style>
        </head>
        <body>
          <div class="info">Live Bracket PES - Bondowoso Berkah (Resolusi Tinggi)</div>
          <img src="${screenshotImgUrl.value}" alt="Bagan Pertandingan" />
        </body>
      </html>
    `);
    w.document.close();
  }
};

const initialLoading = ref(true);

const isOnline = ref(true);

const isFullscreen = ref(false);

const now = ref(new Date());

const bracket = ref(null);

const peserta = ref([]);

const matches = ref([]);

const estimasiTotalSlot = ref(0);

const bracketScroll = ref(null);

const bracketSvg = ref(null);

const selectedRound = ref("all");

const participantSearch = ref("");

const isAuthed = ref(!REQUIRE_LOGIN || sessionStorage.getItem(GATE_SESSION_KEY) === "1");

const loginForm = ref({ username: "", password: "" });

const loginError = ref("");

let pollTimer = null;

let clockTimer = null;

/* =========================================================
   MODE TANGAN (HAND / PAN MODE)
   - Aktif: tarik bagan dengan mouse untuk geser (kiri/kanan & atas/bawah)
     dengan efek inersia.
   - Shortcut: H = Tangan, V = Pilih, tahan Spasi = tangan sementara,
     klik tengah (roda mouse) + tarik = geser kapan saja.
   ========================================================= */
const handMode = ref(false);
const spaceHeld = ref(false);
const isPanning = ref(false);
const handActive = computed(() => handMode.value || spaceHeld.value);

let panStartX = 0;
let panStartY = 0;
let panStartLeft = 0;
let panStartTop = 0;
let panLastX = 0;
let panLastY = 0;
let panLastTime = 0;
let panVelX = 0;
let panVelY = 0;
let panMoved = false;
let panInertiaRaf = null;

const stopPanInertia = () => {
  if (panInertiaRaf) {
    cancelAnimationFrame(panInertiaRaf);
    panInertiaRaf = null;
  }
};

const startPanInertia = () => {
  if (Math.abs(panVelX) < 1 && Math.abs(panVelY) < 1) return;

  const step = () => {
    const el = bracketScroll.value;
    if (!el || isPanning.value || (Math.abs(panVelX) < 0.5 && Math.abs(panVelY) < 0.5)) {
      panInertiaRaf = null;
      return;
    }

    const beforeLeft = el.scrollLeft;
    const beforeTop = el.scrollTop;

    // pointer bergerak ke kanan/bawah = konten ikut = scroll berkurang
    el.scrollLeft -= panVelX;
    el.scrollTop -= panVelY;

    if (el.scrollLeft === beforeLeft) panVelX = 0;
    if (el.scrollTop === beforeTop) panVelY = 0;

    panVelX *= 0.94;
    panVelY *= 0.94;

    panInertiaRaf = requestAnimationFrame(step);
  };

  panInertiaRaf = requestAnimationFrame(step);
};

const onPanMouseMove = (e) => {
  if (!isPanning.value) return;
  const el = bracketScroll.value;
  if (!el) return;

  const dx = e.clientX - panStartX;
  const dy = e.clientY - panStartY;

  if (!panMoved && (Math.abs(dx) > 4 || Math.abs(dy) > 4)) {
    panMoved = true;
  }

  el.scrollLeft = panStartLeft - dx;
  el.scrollTop = panStartTop - dy;

  const nowT = performance.now();
  const dt = nowT - panLastTime;
  if (dt > 0) {
    panVelX = panVelX * 0.6 + ((e.clientX - panLastX) / dt) * 16 * 0.4;
    panVelY = panVelY * 0.6 + ((e.clientY - panLastY) / dt) * 16 * 0.4;
  }
  panLastX = e.clientX;
  panLastY = e.clientY;
  panLastTime = nowT;
};

const endPan = () => {
  if (!isPanning.value) return;
  isPanning.value = false;
  window.removeEventListener("mousemove", onPanMouseMove);
  window.removeEventListener("mouseup", endPan);
  window.removeEventListener("blur", endPan);

  // Kalau pointer sempat diam sebelum dilepas, jangan beri inersia
  if (performance.now() - panLastTime > 80) {
    panVelX = 0;
    panVelY = 0;
  }

  // reset flag "sudah digeser" setelah event click bawaan lewat
  setTimeout(() => {
    panMoved = false;
  }, 0);

  startPanInertia();
};

const onPanMouseDown = (e) => {
  // klik kiri saat mode tangan / Spasi ditahan, atau klik tengah (roda mouse) kapan saja
  const isMiddle = e.button === 1;
  if (!isMiddle && (!handActive.value || e.button !== 0)) return;

  const target = e.target;
  if (
    target &&
    target.closest &&
    target.closest("input, textarea, select, .bracket-quicknav")
  )
    return;

  const el = bracketScroll.value;
  if (!el) return;

  stopPanInertia();

  isPanning.value = true;
  panMoved = false;
  panVelX = 0;
  panVelY = 0;
  panStartX = e.clientX;
  panStartY = e.clientY;
  panLastX = e.clientX;
  panLastY = e.clientY;
  panLastTime = performance.now();
  panStartLeft = el.scrollLeft;
  panStartTop = el.scrollTop;

  window.addEventListener("mousemove", onPanMouseMove);
  window.addEventListener("mouseup", endPan);
  window.addEventListener("blur", endPan);

  // cegah seleksi teks & drag native saat menarik bagan
  e.preventDefault();
};

// Selama mode tangan: blok drag native (gambar/teks)
const onPanDragStartCapture = (e) => {
  if (handActive.value) {
    e.preventDefault();
    e.stopPropagation();
  }
};

// Kalau pointer sudah bergeser (artinya user menarik bagan), jangan anggap itu klik
const onPanClickCapture = (e) => {
  if (panMoved) {
    e.preventDefault();
    e.stopPropagation();
    panMoved = false;
  }
};

const setHandMode = (value) => {
  if (handMode.value === value) return;
  handMode.value = value;
  if (!value) {
    endPan();
    stopPanInertia();
  }
};

const isTypingTarget = (el) => {
  if (!el) return false;
  const tag = (el.tagName || "").toLowerCase();
  return (
    tag === "input" || tag === "textarea" || tag === "select" || el.isContentEditable
  );
};

const shortcutAllowed = (e) => {
  if (e.ctrlKey || e.metaKey || e.altKey) return false;
  if (isTypingTarget(e.target)) return false;
  if (screenshotDialogOpen.value) return false;
  if (!isAuthed.value) return false;
  if (selectedRound.value !== "all") return false;
  return true;
};

// H = Tangan, V = Pilih (kursor), tahan Spasi = tangan sementara
const handleHandModeKey = (e) => {
  if (!shortcutAllowed(e)) return;

  if (e.code === "Space") {
    e.preventDefault(); // cegah halaman ikut scroll / menekan tombol fokus
    if (!spaceHeld.value) {
      spaceHeld.value = true;
      stopPanInertia();
    }
    return;
  }

  if (e.repeat) return;
  const key = (e.key || "").toLowerCase();
  if (key === "h") setHandMode(true);
  else if (key === "v") setHandMode(false);
};

const handleHandModeKeyUp = (e) => {
  if (e.code !== "Space" || !spaceHeld.value) return;
  e.preventDefault();
  spaceHeld.value = false;
  if (!handMode.value) endPan();
};

const releaseSpace = () => {
  spaceHeld.value = false;
};

const currentDate = computed(() =>
  now.value.toLocaleDateString("id-ID", {
    weekday: "long",

    day: "2-digit",

    month: "long",

    year: "numeric",
  })
);

const currentTime = computed(() =>
  now.value.toLocaleTimeString("id-ID", {
    hour: "2-digit",

    minute: "2-digit",

    second: "2-digit",
  })
);

const totalSlotUntukProyeksi = computed(() =>
  peserta.value.length ? peserta.value.length : estimasiTotalSlot.value
);

const liveCount = computed(
  () =>
    matches.value.filter(
      (m) =>
        m.status === "menunggu" &&
        m.peserta1_id &&
        m.peserta2_id &&
        m.jadwal_mulai &&
        new Date(String(m.jadwal_mulai).replace(" ", "T")).getTime() <= Date.now()
    ).length
);

const nextMatches = computed(() =>
  matches.value

    .filter(
      (m) =>
        m.status === "menunggu" &&
        m.peserta1_id &&
        m.peserta2_id &&
        m.jadwal_mulai &&
        new Date(String(m.jadwal_mulai).replace(" ", "T")).getTime() > Date.now()
    )

    .sort(
      (a, b) =>
        new Date(String(a.jadwal_mulai).replace(" ", "T")) -
        new Date(String(b.jadwal_mulai).replace(" ", "T"))
    )

    .slice(0, 3)
);

const realRoundGroups = computed(() => {
  const grouped = {};

  matches.value.forEach((m) => {
    if (m.ronde >= 900) return;

    (grouped[m.ronde] ||= []).push(m);
  });

  Object.values(grouped).forEach((list) =>
    list.sort((a, b) => (a.urutan || 0) - (b.urutan || 0))
  );

  return grouped;
});

const maxRealRound = computed(() => {
  const rounds = Object.keys(realRoundGroups.value).map(Number).filter(Number.isFinite);

  return rounds.length ? Math.max(...rounds) : 0;
});

const roundNavigation = computed(() => {
  const rounds = [];

  const maxRound = Math.max(
    maxRealRound.value,

    projectedRounds.value.length ? projectedRounds.value.length : 0
  );

  for (let r = 1; r <= maxRound; r++) {
    const count =
      realRoundGroups.value[r]?.length ||
      projectedRounds.value.find((x) => x.ronde === r)?.matchCount ||
      0;

    rounds.push({
      value: r,

      label: r === maxRound ? "Semifinal" : `Babak ${r}`,

      count,
    });
  }

  if (maxRound > 0) {
    rounds.push({
      value: 901,

      label: "Perebutan Juara 3",

      count: juara3Match.value ? 1 : 0,
    });

    rounds.push({
      value: 900,

      label: "Grand Final",

      count: finalMatch.value ? 1 : 0,
    });
  }

  return rounds;
});

const selectedRoundLabel = computed(() => {
  if (selectedRound.value === "all") return "Semua Babak";

  return (
    roundNavigation.value.find((r) => r.value === selectedRound.value)?.label ||
    labelRonde(selectedRound.value)
  );
});

const selectedRoundMatches = computed(() => {
  if (selectedRound.value === "all") return [];

  if (selectedRound.value === 900) {
    return finalMatch.value ? [finalMatch.value] : [];
  }

  if (selectedRound.value === 901) {
    return juara3Match.value ? [juara3Match.value] : [];
  }

  return [...(realRoundGroups.value[selectedRound.value] || [])].sort(
    (a, b) => (a.urutan || 0) - (b.urutan || 0)
  );
});

const filteredSelectedRoundMatches = computed(() => {
  const q = participantSearch.value.trim().toLowerCase();

  if (!q) return selectedRoundMatches.value;

  return selectedRoundMatches.value.filter((m) => {
    const n1 = namaPeserta(m.peserta1_id).toLowerCase();

    const n2 = namaPeserta(m.peserta2_id).toLowerCase();

    return n1.includes(q) || n2.includes(q);
  });
});

const searchResults = computed(() => {
  const q = participantSearch.value.trim().toLowerCase();

  if (!q) return [];

  return matches.value

    .filter((m) => m.ronde < 900)

    .filter((m) => {
      const n1 = namaPeserta(m.peserta1_id).toLowerCase();

      const n2 = namaPeserta(m.peserta2_id).toLowerCase();

      return n1.includes(q) || n2.includes(q);
    })

    .sort((a, b) => {
      const ar = a.ronde - b.ronde;

      return ar || (a.urutan || 0) - (b.urutan || 0);
    });
});

const isSearchMatch = (m) => {
  const q = participantSearch.value.trim().toLowerCase();

  if (!q) return false;

  return (
    namaPeserta(m.peserta1_id).toLowerCase().includes(q) ||
    namaPeserta(m.peserta2_id).toLowerCase().includes(q)
  );
};

const selectRound = async (round) => {
  selectedRound.value = round;

  await nextTick();

  if (round === "all") {
    requestAnimationFrame(drawBracketLines);
  }
};

const jumpToSearchResult = async (match) => {
  if (!match) return;

  selectedRound.value = match.ronde;

  await nextTick();

  requestAnimationFrame(() => {
    const el = document.querySelector(`[data-round-match-id="${match.id}"]`);

    el?.scrollIntoView({ behavior: "smooth", block: "center" });
  });
};

const handleLogin = () => {
  if (
    loginForm.value.username === GATE_USERNAME &&
    loginForm.value.password === GATE_PASSWORD
  ) {
    isAuthed.value = true;

    loginError.value = "";

    sessionStorage.setItem(GATE_SESSION_KEY, "1");
  } else loginError.value = "Username atau password salah.";
};

const fetchBracket = async ({ silent = false } = {}) => {
  try {
    const { data } = await axios.get(`${API_BASE}/esport/bracket`, {
      ...authHeaders,

      params: { jenis_lomba: JENIS_LOMBA },
    });

    const payload = data?.response?.data ?? data?.data ?? null;

    if (payload?.bracket) {
      bracket.value = payload.bracket;

      peserta.value = payload.peserta || [];

      matches.value = payload.matches || [];
    } else {
      bracket.value = null;

      peserta.value = [];

      matches.value = [];

      await fetchEstimasiPeserta();
    }

    isOnline.value = true;

    if (
      selectedRound.value !== "all" &&
      !roundNavigation.value.some((r) => r.value === selectedRound.value)
    ) {
      selectedRound.value = "all";
    }

    await nextTick();

    requestAnimationFrame(() => drawBracketLines());
  } catch (err) {
    console.error("Gagal memuat bracket (live)", err);

    isOnline.value = false;
  } finally {
    if (!silent) initialLoading.value = false;
  }
};

const fetchEstimasiPeserta = async () => {
  try {
    const { data } = await axios.get(`${API_BASE}/esport/pendaftar`, {
      ...authHeaders,
      params: { jenis_lomba: JENIS_LOMBA },
    });

    const list = data?.response?.data ?? data?.data ?? [];

    estimasiTotalSlot.value = list
      .filter((p) => p.jenis_lomba === JENIS_LOMBA && Number(p.status_verifikasi) === 1)
      .reduce((sum, p) => sum + Math.max(1, Number(p.jumlah_slot) || 1), 0);
  } catch (err) {
    console.error("Gagal memuat estimasi peserta", err);
    estimasiTotalSlot.value = 0;
  }
};

const namaPeserta = (id) => {
  if (!id) return "—";

  const p = peserta.value.find((x) => x.id === id);

  return p ? p.nama_display : "—";
};

const hitungByeAdilSisi = (n) => {
  if (n <= 2) return 0;

  const setengah = Math.ceil(n / 2);

  const target = setengah % 2 === 0 ? setengah : setengah + 1;

  return 2 * target - n;
};

const projectSideRounds = (n) => {
  const counts = [];

  let cur = n,
    first = true,
    guard = 0;

  if (n < 1) return counts;

  while (cur > 1) {
    const bye = first ? hitungByeAdilSisi(cur) : cur % 2 !== 0 ? 1 : 0;

    const main = cur - bye;

    const mc = Math.max(1, Math.ceil(main / 2));

    counts.push(mc + bye);

    cur = mc + bye;

    first = false;

    if (++guard > 30) break;
  }

  return counts;
};

const sideSplit = computed(() => {
  const n = totalSlotUntukProyeksi.value;

  if (!n || n < 2) return { nLeft: 0, nRight: 0 };

  const nLeft = Math.ceil(n / 2);

  return { nLeft, nRight: n - nLeft };
});

const projectedRounds = computed(() => {
  let n = totalSlotUntukProyeksi.value,
    rounds = [],
    ronde = 1;

  if (!n || n < 2) return rounds;

  while (n > 2) {
    const bye = ronde === 1 ? hitungByeAdilSisi(n) : n % 2 !== 0 ? 1 : 0;

    const mc = (n - bye) / 2;

    if (mc <= 0) break;

    rounds.push({ ronde, matchCount: mc });

    n = mc + bye;

    if (++ronde > 30) break;
  }

  return rounds;
});

const placeholderMatch = (side, ronde, idx) => ({
  id: `placeholder-${side}-${ronde}-${idx}`,

  ronde,

  urutan: idx,

  status: "placeholder",

  peserta1_id: null,

  peserta2_id: null,

  pemenang_id: null,

  isPlaceholder: true,
});

const buildSideColumns = (side, realByRonde, counts, totalRoundsReal) => {
  const totalRounds = Math.max(counts.length, totalRoundsReal, 1),
    cols = [];

  for (let r = 1; r <= totalRounds; r++) {
    const real = realByRonde[r];

    const roundMatches = real?.length
      ? real
      : Array.from({ length: counts[r - 1] ?? (counts.length ? 1 : 0) }, (_, i) =>
          placeholderMatch(side, r, i)
        );

    cols.push({
      ronde: r,

      label: r === totalRounds ? "Semifinal" : `Babak ${r}`,

      matches: roundMatches,
    });
  }

  return cols;
};

const sideRounds = computed(() => {
  const left = {},
    right = {};

  matches.value.forEach((m) => {
    if (m.ronde >= 900) return;

    if (m.sisi === "kiri") (left[m.ronde] = left[m.ronde] || []).push(m);
    else if (m.sisi === "kanan") (right[m.ronde] = right[m.ronde] || []).push(m);
  });

  Object.values(left).forEach((x) => x.sort((a, b) => a.urutan - b.urutan));

  Object.values(right).forEach((x) => x.sort((a, b) => a.urutan - b.urutan));

  const { nLeft, nRight } = sideSplit.value;

  const l = buildSideColumns(
    "left",

    left,

    projectSideRounds(nLeft),

    Object.keys(left).length
  );

  const r = buildSideColumns(
    "right",

    right,

    projectSideRounds(nRight),

    Object.keys(right).length
  );

  r.reverse();

  return { left: l, right: r };
});

const finalMatch = computed(() => matches.value.find((m) => m.ronde === 900) || null);

const juara3Match = computed(() => matches.value.find((m) => m.ronde === 901) || null);

const finalDisplay = computed(
  () =>
    finalMatch.value || {
      id: "placeholder-final",

      ronde: 900,

      urutan: 0,

      status: "placeholder",

      peserta1_id: null,

      peserta2_id: null,

      pemenang_id: null,

      isPlaceholder: true,
    }
);

const juara3Display = computed(
  () =>
    juara3Match.value ||
    (projectedRounds.value.length
      ? {
          id: "placeholder-j3",

          ronde: 901,

          urutan: 0,

          status: "placeholder",

          peserta1_id: null,

          peserta2_id: null,

          pemenang_id: null,

          isPlaceholder: true,
        }
      : null)
);

const labelRonde = (ronde) =>
  ronde === 900
    ? "Grand Final"
    : ronde === 901
    ? "Juara 3"
    : ronde === 899
    ? "Semifinal"
    : `Babak ${ronde}`;

const formatSchedule = (value) => {
  if (!value) return "Jadwal belum ditentukan";

  const d = new Date(String(value).replace(" ", "T"));

  return Number.isNaN(d.getTime())
    ? value
    : d.toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" }) + " WIB";
};

const createPath = (svg, d) => {
  const path = document.createElementNS("http://www.w3.org/2000/svg", "path");

  path.setAttribute("d", d);

  path.setAttribute("class", "bracket-connector");

  path.setAttribute("fill", "none");

  path.setAttribute("stroke", "#028c73");

  path.setAttribute("stroke-width", "3");

  path.setAttribute("stroke-linecap", "round");

  path.setAttribute("stroke-linejoin", "round");

  path.setAttribute("opacity", "0.8");

  svg.appendChild(path);
};

const drawLineBetween = (curEl, nextEl, container, svg) => {
  const cr = curEl.getBoundingClientRect(),
    nr = nextEl.getBoundingClientRect(),
    rr = container.getBoundingClientRect();

  const right = cr.left + cr.width / 2 < nr.left + nr.width / 2;

  const sx = (right ? cr.right : cr.left) - rr.left + container.scrollLeft,
    sy = cr.top - rr.top + cr.height / 2 + container.scrollTop,
    ex = (right ? nr.left : nr.right) - rr.left + container.scrollLeft,
    ey = nr.top - rr.top + nr.height / 2 + container.scrollTop,
    mx = sx + (ex - sx) / 2;

  createPath(svg, `M ${sx} ${sy} H ${mx} V ${ey} H ${ex}`);
};

const connectByAsalMatch = (container, svg) => {
  const set = new Set();

  matches.value.forEach((m) => {
    if (m.ronde === 1) return;

    const next = container.querySelector(`[data-match-id="${m.id}"]`);

    if (!next) return;

    [m.asal_match1_id, m.asal_match2_id].forEach((id) => {
      if (!id) return;

      const key = `${id}->${m.id}`;

      if (set.has(key)) return;

      const cur = container.querySelector(`[data-match-id="${id}"]`);

      if (cur) {
        drawLineBetween(cur, next, container, svg);

        set.add(key);
      }
    });
  });

  return new Set(matches.value.filter((m) => m.ronde > 1).map((m) => m.id));
};

const connectPlaceholderFallback = (columns, container, svg, realSet) => {
  for (let i = 0; i < columns.length - 1; i++) {
    const cur = [...columns[i].querySelectorAll(".esport-match-card-view")],
      next = [...columns[i + 1].querySelectorAll(".esport-match-card-view")];

    cur.forEach((card, idx) => {
      const n = next[Math.floor(idx / 2)];

      if (!n) return;

      const id = Number(n.dataset.matchId);

      if (!Number.isNaN(id) && realSet.has(id)) return;

      drawLineBetween(card, n, container, svg);
    });
  }
};

const connectToFinalPlaceholder = (col, container, svg, realSet) => {
  if (!col) return;

  const final = container.querySelector(".final-card-wrap .esport-match-card-view"),
    card = col.querySelector(".esport-match-card-view");

  if (!final || !card) return;

  const id = Number(final.dataset.matchId);

  if (!Number.isNaN(id) && realSet.has(id)) return;

  drawLineBetween(card, final, container, svg);
};

const drawBracketLines = (targetContainer, targetSvg) => {
  const isCustomEl =
    targetContainer &&
    typeof targetContainer === "object" &&
    targetContainer.nodeType === 1;
  if (selectedRound.value !== "all" && !isCustomEl) return;

  const container = isCustomEl ? targetContainer : bracketScroll.value;
  const svg =
    targetSvg && typeof targetSvg === "object" && targetSvg.nodeType === 1
      ? targetSvg
      : bracketSvg.value;

  if (!container || !svg) return;

  svg.style.width = container.scrollWidth + "px";
  svg.style.height = container.scrollHeight + "px";
  svg.setAttribute("width", container.scrollWidth + "px");
  svg.setAttribute("height", container.scrollHeight + "px");
  svg.setAttribute("viewBox", `0 0 ${container.scrollWidth} ${container.scrollHeight}`);

  svg.innerHTML = "";

  const left = [...container.querySelectorAll(".bracket-half-left .bracket-column")];
  const right = [
    ...container.querySelectorAll(".bracket-half-right .bracket-column"),
  ].reverse();

  const real = connectByAsalMatch(container, svg);

  if (left.length > 1) connectPlaceholderFallback(left, container, svg, real);

  if (right.length > 1) connectPlaceholderFallback(right, container, svg, real);

  if (left.length) connectToFinalPlaceholder(left[left.length - 1], container, svg, real);

  if (right.length)
    connectToFinalPlaceholder(right[right.length - 1], container, svg, real);
};

const scrollToBracketSide = (target) => {
  const vp = bracketScroll.value;
  if (!vp) return;

  stopPanInertia();

  const behavior = "smooth";

  if (target === "left") {
    vp.scrollTo({ left: 0, behavior });
    return;
  }

  if (target === "right") {
    vp.scrollTo({ left: vp.scrollWidth, behavior });
    return;
  }

  const centerEl = vp.querySelector(".bracket-center");
  if (centerEl) {
    const vpRect = vp.getBoundingClientRect();
    const elRect = centerEl.getBoundingClientRect();
    const scrollLeft =
      vp.scrollLeft + (elRect.left - vpRect.left) - vpRect.width / 2 + elRect.width / 2;
    vp.scrollTo({ left: scrollLeft, behavior });
  } else {
    vp.scrollTo({ left: vp.scrollWidth / 2 - vp.clientWidth / 2, behavior });
  }
};

const toggleFullscreen = () => {
  if (!document.fullscreenElement) document.documentElement.requestFullscreen?.();
  else document.exitFullscreen?.();
};

const handleFullscreenChange = () => {
  isFullscreen.value = !!document.fullscreenElement;
};

const sharePage = async () => {
  try {
    if (navigator.share)
      await navigator.share({
        title: "PES Live Score - Bondowoso Berkah",

        url: window.location.href,
      });
    else {
      await navigator.clipboard.writeText(window.location.href);

      alert("Link live score berhasil disalin.");
    }
  } catch (e) {}
};

onMounted(async () => {
  await fetchBracket();

  pollTimer = setInterval(() => fetchBracket({ silent: true }), POLL_INTERVAL_MS);

  clockTimer = setInterval(() => (now.value = new Date()), 1000);

  window.addEventListener("resize", () =>
    requestAnimationFrame(() => drawBracketLines())
  );

  document.addEventListener("fullscreenchange", handleFullscreenChange);

  window.addEventListener("keydown", handleHandModeKey);
  window.addEventListener("keyup", handleHandModeKeyUp);
  window.addEventListener("blur", releaseSpace);
});

onBeforeUnmount(() => {
  if (pollTimer) clearInterval(pollTimer);

  if (clockTimer) clearInterval(clockTimer);

  document.removeEventListener("fullscreenchange", handleFullscreenChange);

  window.removeEventListener("keydown", handleHandModeKey);
  window.removeEventListener("keyup", handleHandModeKeyUp);
  window.removeEventListener("blur", releaseSpace);
  window.removeEventListener("mousemove", onPanMouseMove);
  window.removeEventListener("mouseup", endPan);
  window.removeEventListener("blur", endPan);
  stopPanInertia();
});
</script>

<style scoped>
.esport-live-app {
  background: transparent !important;
  font-family: inherit !important;
}

.esport-live-page {
  --teal: #028c73;

  --teal-dark: #016653;

  --teal-deep: #103b33;

  --teal-soft: #e8f6f2;

  --border: #d7e7e2;

  --text: #173b34;

  --muted: #6b817b;

  --gold: #b77d16;

  position: fixed;

  inset: 0;

  width: 100vw;

  height: 100vh;

  overflow: hidden;

  display: flex;

  flex-direction: column;

  background: #f7faf9;

  color: var(--text);

  font-family: Georgia, "Times New Roman", serif;
}

.esport-live-page * {
  box-sizing: border-box;
}

.live-topbar {
  height: 68px;

  flex: 0 0 68px;

  display: flex;

  align-items: center;

  justify-content: space-between;

  padding: 0 20px;

  background: linear-gradient(135deg, #028c73, #016653);

  border-bottom: 3px solid #b77d16;

  box-shadow: 0 2px 12px rgba(1, 60, 48, 0.25);

  position: relative;

  z-index: 20;
}

.brand-area,
.header-actions,
.game-title-wrap,
.date-clock,
.live-status {
  display: flex;

  align-items: center;
}

.brand-area {
  gap: 10px;

  min-width: 0;
}

.live-logo {
  width: 40px;

  height: 40px;

  object-fit: contain;

  flex: 0 0 40px;
}

.brand-text {
  display: flex;

  flex-direction: column;

  line-height: 1.15;

  min-width: 145px;
}

.brand-text strong {
  font-size: 15px;

  color: #ffffff;
}

.brand-subtitle-desktop {
  font-size: 8px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.75);
  margin-top: 3px;
}

.brand-subtitle-mobile {
  display: none;

  color: rgba(255, 255, 255, 0.85);
}

.status-short {
  display: none;
}

.game-divider {
  width: 1px;

  height: 38px;

  background: rgba(255, 255, 255, 0.3);

  margin: 0 10px;
}

.game-title-wrap {
  gap: 9px;
}

.game-icon {
  width: 39px;

  height: 39px;

  border: 1px solid rgba(255, 255, 255, 0.35);

  border-radius: 11px;

  display: flex;

  align-items: center;

  justify-content: center;

  color: #ffffff;

  background: rgba(255, 255, 255, 0.08);

  flex: 0 0 39px;
}

.game-title-wrap small {
  font-size: 7px;

  font-weight: 900;

  color: rgba(255, 255, 255, 0.7);

  letter-spacing: 1px;
}

.game-title-wrap h1 {
  margin: 2px 0 0;

  font-size: 14px;

  letter-spacing: 0.3px;

  color: #ffffff;
}

.game-title-wrap h1 b {
  font-size: 7px;

  color: #ffffff;

  background: #e44d4d;

  padding: 3px 6px;

  border-radius: 5px;

  vertical-align: middle;

  margin-left: 5px;
}

.header-actions {
  gap: 10px;
}

.date-clock {
  gap: 15px;

  font-size: 14px;

  color: rgba(255, 255, 255, 0.85);

  white-space: nowrap;
}

.date-clock span {
  display: flex;

  align-items: center;

  gap: 4px;
}

.live-status {
  gap: 6px;

  padding: 7px 11px;

  border: 1px solid rgba(255, 255, 255, 0.4);

  border-radius: 999px;

  background: rgba(255, 255, 255, 0.12);

  color: #ffffff;

  font-size: 8px;

  font-weight: 900;

  letter-spacing: 0.4px;

  white-space: nowrap;
}

.live-status.offline {
  color: #fef3c7;

  border-color: rgba(255, 255, 255, 0.4);

  background: rgba(0, 0, 0, 0.12);
}

.live-status-dot,
.refresh-dot {
  width: 6px;

  height: 6px;

  border-radius: 50%;

  background: #ff2c2c;

  box-shadow: 0 0 0 3px rgba(255, 220, 168, 0.15);
}

.live-status-dot {
  position: relative;

  flex: 0 0 auto;

  animation: live-dot-pulse 1.4s ease-in-out infinite;
}

.live-status-dot::after {
  content: "";

  position: absolute;

  inset: -4px;

  border-radius: 50%;

  background: rgba(255, 168, 168, 0.468);

  animation: live-dot-ring 1.4s ease-out infinite;
}

.live-status.offline .live-status-dot,
.live-status.offline .live-status-dot::after {
  animation: none;

  background: #f3d38a;
}

@keyframes live-dot-pulse {
  0%,
  100% {
    opacity: 1;

    transform: scale(1);
  }

  50% {
    opacity: 0.55;

    transform: scale(0.82);
  }
}

@keyframes live-dot-ring {
  0% {
    opacity: 0.55;

    transform: scale(0.4);
  }

  100% {
    opacity: 0;

    transform: scale(1.8);
  }
}

.refresh-dot {
  background: var(--teal);

  box-shadow: none;
}

.icon-btn {
  width: 36px;

  height: 36px;

  border: 1px solid rgba(255, 255, 255, 0.35);

  background: rgba(255, 255, 255, 0.1);

  border-radius: 10px;

  color: #ffffff;

  cursor: pointer;

  transition: background 0.2s ease, border-color 0.2s ease, transform 0.2s ease;
}

.header-screenshot-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 36px;
  padding: 0 12px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.35);
  background: rgba(255, 255, 255, 0.1);
  color: #ffffff;
  font-size: 11.5px;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  transition: all 0.18s ease;
  white-space: nowrap;
}

.header-screenshot-btn:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.22);
  border-color: rgba(255, 255, 255, 0.6);
  transform: translateY(-1px);
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.15);
}

.header-screenshot-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.icon-btn:hover {
  background: rgba(255, 255, 255, 0.22);

  border-color: rgba(255, 255, 255, 0.55);

  transform: translateY(-1px);
}

.champion-banner {
  min-height: 46px;

  flex: 0 0 46px;

  padding: 6px 16px;

  display: flex;

  align-items: center;

  gap: 8px;

  background: #fffdf7;

  border-bottom: 1px solid #eee1c6;

  overflow-x: auto;

  overflow-y: hidden;

  scrollbar-width: thin;

  scrollbar-color: #e2d2b0 transparent;

  -webkit-overflow-scrolling: touch;
}

.champion-banner::-webkit-scrollbar {
  height: 4px;
}

.champion-banner::-webkit-scrollbar-thumb {
  background: #e2d2b0;

  border-radius: 999px;
}

.champion-title {
  display: flex;

  align-items: center;

  gap: 5px;

  color: #9b701c;

  font-size: 8px;

  font-weight: 900;

  margin-right: 5px;

  white-space: nowrap;

  flex-shrink: 0;
}

.champion-item {
  height: 32px;

  display: flex;

  align-items: center;

  gap: 7px;

  padding: 4px 10px;

  border-radius: 999px;

  background: #ffffff;

  border: 1px solid #e4e8e5;

  font-size: 8px;

  white-space: nowrap;

  flex-shrink: 0;

  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.champion-item:hover {
  transform: translateY(-1px);

  box-shadow: 0 4px 12px rgba(50, 70, 60, 0.08);
}

.champion-item strong {
  font-size: 9px;

  max-width: 180px;

  overflow: hidden;

  text-overflow: ellipsis;
}

.champion-item.c1 {
  border-color: #eed8a3;
}

.champion-item.c1 v-icon {
  color: #c89124;
}

.champion-item.c2 {
  border-color: #d9e0de;
}

.champion-item.c3 {
  border-color: #ead5bd;
}

.champion-item.c4 {
  border-color: #cfe2dc;
}

.live-content {
  flex: 1 1 auto;

  height: auto;

  min-height: 0;

  display: flex;

  flex-direction: column;

  overflow: hidden;
}

.live-toolbar {
  height: 66px;

  flex: 0 0 66px;

  display: flex;

  align-items: center;

  justify-content: space-between;

  padding: 8px 20px;

  background: #ffffff;

  border-bottom: 1px solid #e0ebe7;
}

.toolbar-title {
  display: flex;

  align-items: center;

  gap: 10px;
}

.section-kicker {
  font-size: 7px;

  font-weight: 900;

  color: #4b766b;

  letter-spacing: 1px;
}

.toolbar-title h2 {
  font-size: 12px;

  margin: 0;

  display: flex;

  align-items: center;

  gap: 5px;
}

.legend {
  font-size: 7px;

  color: #80918d;

  border-left: 1px solid #e0ebe7;

  padding-left: 10px;
}

.legend-dot {
  display: inline-block;

  width: 6px;

  height: 6px;

  border-radius: 50%;

  background: var(--teal);

  margin: 0 4px;
}

.legend-line {
  display: inline-block;

  width: 18px;

  height: 2px;

  background: var(--teal);

  margin: 0 4px 2px;
}

.toolbar-actions {
  display: flex;

  align-items: center;

  gap: 8px;
}

.refresh-control {
  display: flex;

  align-items: center;

  gap: 6px;

  padding: 7px 9px;

  border: 1px solid #d8e7e2;

  border-radius: 9px;

  background: #fbfdfc;

  font-size: 8px;

  color: #58746c;
}

.refresh-control b {
  color: var(--teal);

  font-size: 7px;
}

.share-btn {
  border: 0;

  background: var(--teal);

  color: #ffffff;

  border-radius: 9px;

  padding: 8px 12px;

  font-size: 8px;

  font-weight: 900;

  cursor: pointer;

  display: flex;

  align-items: center;

  gap: 5px;

  transition: background 0.2s ease, transform 0.2s ease;
}

.share-btn:hover {
  background: var(--teal-dark);

  transform: translateY(-1px);
}

.belum-generate-note {
  margin: 8px 20px;

  padding: 7px 10px;

  border: 1px solid #d5e7e1;

  background: #eff8f5;

  border-radius: 8px;

  font-size: 8px;

  color: #54756c;

  display: flex;

  align-items: center;

  gap: 6px;
}

.live-loading {
  position: absolute;

  inset: 135px 0 0;

  display: flex;

  align-items: center;

  justify-content: center;

  flex-direction: column;

  gap: 10px;

  color: #638079;

  font-size: 10px;
}

.bracket-shell {
  position: relative;

  flex: 1 1 auto;

  min-height: 0;

  padding: 0 12px;

  display: flex;

  flex-direction: column;

  overflow: hidden;
}

.bracket-viewport {
  position: relative;

  flex: 1 1 auto;

  min-height: 0;

  height: auto;

  overflow: auto;

  padding: 13px 8px 40px;

  scrollbar-gutter: stable;

  background: linear-gradient(rgba(236, 247, 244, 0.78), rgba(236, 247, 244, 0.78)),
    url("/images/bglivepes.png");

  background-size: cover;

  background-position: center;

  background-repeat: no-repeat;

  scrollbar-color: #7dbdb1 transparent;

  scrollbar-width: thin;
}

/* ===== Mode Tangan: kursor grab, tanpa seleksi teks ===== */
.bracket-viewport.hand-mode {
  cursor: grab;
  user-select: none;
  -webkit-user-select: none;
}
.bracket-viewport.hand-mode :deep(*) {
  cursor: grab !important;
  user-select: none !important;
  -webkit-user-select: none !important;
  -webkit-user-drag: none;
}
.bracket-viewport.hand-mode .bracket-quicknav,
.bracket-viewport.hand-mode .bracket-quicknav * {
  cursor: pointer !important;
}
.bracket-viewport.is-panning,
.bracket-viewport.is-panning :deep(*) {
  cursor: grabbing !important;
  user-select: none !important;
  -webkit-user-select: none !important;
}

.hand-mode-hint {
  position: absolute;
  left: 50%;
  bottom: 22px;
  transform: translateX(-50%);
  z-index: 26;
  pointer-events: none;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.82);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  color: #ffffff;
  font-size: 10px;
  font-weight: 700;
  white-space: nowrap;
  box-shadow: 0 8px 22px rgba(15, 23, 42, 0.28);
}
.hand-mode-hint em {
  font-style: normal;
  font-weight: 600;
  color: #94a3b8;
  margin-left: 4px;
}

.bracket-quicknav {
  position: sticky;
  top: 10px;
  left: 0;
  right: 0;
  z-index: 20;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 6px;
  pointer-events: none;
  margin-bottom: 6px;
}

/* ===== Toggle Mode Pilih / Tangan ===== */
.mode-pill-group {
  pointer-events: all;
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 2px;
  border: 1.5px solid rgba(2, 140, 115, 0.4);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.88);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  box-shadow: 0 2px 8px rgba(2, 140, 115, 0.12);
}
.mode-pill-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 11px;
  border: 0;
  border-radius: 999px;
  background: transparent;
  color: var(--teal-dark);
  font-family: inherit;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.04em;
  line-height: 1.2;
  cursor: pointer;
  transition: background 0.18s ease, color 0.18s ease, box-shadow 0.18s ease;
  user-select: none;
}
.mode-pill-btn .v-icon {
  color: inherit !important;
}
.mode-pill-btn:hover {
  background: rgba(2, 140, 115, 0.12);
}
.mode-pill-btn.active {
  background: var(--teal);
  color: #ffffff;
  box-shadow: 0 3px 9px rgba(2, 140, 115, 0.3);
}
.mode-kbd {
  display: inline-block;
  min-width: 14px;
  padding: 2px 4px;
  border-radius: 4px;
  border: 1px solid rgba(100, 116, 139, 0.35);
  background: rgba(255, 255, 255, 0.7);
  font-family: inherit;
  font-size: 8px;
  font-weight: 800;
  line-height: 1;
  text-align: center;
  color: #64748b;
}
.mode-pill-btn.active .mode-kbd {
  border-color: rgba(255, 255, 255, 0.45);
  background: rgba(255, 255, 255, 0.18);
  color: #ffffff;
}

.bqn-btn {
  pointer-events: all;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 6px 14px;
  border: 1.5px solid rgba(2, 140, 115, 0.4);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.88);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  color: var(--teal-dark);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.04em;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(2, 140, 115, 0.12);
  transition: background 0.18s ease, border-color 0.18s ease, transform 0.18s ease,
    box-shadow 0.18s ease;
  user-select: none;
}

.bqn-btn:hover {
  background: rgba(2, 140, 115, 0.12);
  border-color: var(--teal);
  transform: translateY(-1px);
  box-shadow: 0 4px 14px rgba(2, 140, 115, 0.22);
}

.bqn-btn:active {
  transform: scale(0.97);
}

.bqn-center {
  background: rgba(2, 140, 115, 0.1);
  border-color: var(--teal);
  color: var(--teal-dark);
}

.bqn-center:hover {
  background: var(--teal);
  color: #fff;
}

.bqn-screenshot {
  background: #ffffff;
  border-color: var(--teal);
  color: var(--teal-dark);
}

.bqn-screenshot:hover {
  background: var(--teal);
  color: #ffffff;
}

.bqn-screenshot:hover .v-icon {
  color: #ffffff !important;
}

.bracket-viewport::-webkit-scrollbar {
  height: 10px;

  width: 10px;
}

.bracket-viewport::-webkit-scrollbar-thumb {
  background: #9ccbc0;

  border-radius: 999px;
}

.bracket-viewport::-webkit-scrollbar-track {
  background: transparent;
}

.bracket-svg {
  position: absolute;

  left: 0;

  top: 0;

  pointer-events: none;

  z-index: 1;
}

.bracket-connector {
  pointer-events: none;
}

.bracket-track {
  position: relative;

  z-index: 2;

  min-width: max-content;

  display: flex;

  align-items: stretch;

  gap: 34px;

  padding: 10px 10px 32px;

  min-height: 100%;
}

.bracket-side-group {
  display: flex;

  flex-direction: column;

  gap: 9px;

  align-self: stretch;
}

.bracket-side-group-left {
  order: 1;
}

.bracket-center {
  order: 2;

  width: 300px;

  min-width: 300px;

  display: flex;

  flex-direction: column;

  align-items: center;

  justify-content: center;

  gap: 25px;

  align-self: stretch;
}

.bracket-side-group-right {
  order: 3;

  align-items: flex-end;
}

.bracket-side-title {
  height: 28px;

  display: flex;

  align-items: center;

  gap: 7px;

  padding: 0 6px;

  color: #1a4a3e;

  font-size: 10px;

  font-weight: 900;

  letter-spacing: 1px;

  flex: 0 0 28px;
}

.bracket-half {
  display: flex;

  align-items: stretch;

  gap: 48px;

  flex: 1;

  min-height: 0;
}

.bracket-half-right {
  flex-direction: row;
}

.bracket-column {
  flex: 0 0 250px;

  width: 250px;

  min-width: 250px;

  display: flex;

  flex-direction: column;

  min-height: 0;
}

.bracket-column-header {
  height: 37px;

  flex: 0 0 37px;

  display: flex;

  align-items: center;

  gap: 7px;

  padding: 0 5px;

  color: #1a4a3e;

  font-size: 9.5px;

  font-weight: 900;

  text-transform: uppercase;

  letter-spacing: 0.9px;

  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
}

.bracket-column-header span {
  white-space: nowrap;
}

.bracket-half-right .bracket-column-header {
  justify-content: flex-end;
}

.round-icon {
  width: 25px;

  height: 25px;

  display: flex;

  align-items: center;

  justify-content: center;

  flex: 0 0 25px;

  border-radius: 8px;

  color: var(--teal);

  background: #ffffff;

  border: 1px solid #c8e0d9;

  box-shadow: 0 2px 7px rgba(13, 68, 56, 0.07);
}

.bracket-column-body {
  flex: 1;

  min-height: 0;

  display: flex;

  flex-direction: column;

  justify-content: space-around;

  gap: 0;

  width: 100%;
}

:deep(.esport-match-card-view) {
  background: #ffffff !important;

  border-color: #9eccc2 !important;

  color: #173b34 !important;

  box-shadow: 0 6px 18px rgba(10, 60, 50, 0.12), 0 2px 4px rgba(10, 60, 50, 0.07) !important;

  flex: 0 0 auto;
}

.final-block {
  width: 100%;

  display: flex;

  flex-direction: column;

  align-items: center;

  padding: 14px 10px 16px;

  border-radius: 18px;

  background: #ffffff;

  border: 2px solid rgba(183, 125, 22, 0.45);

  box-shadow: 0 14px 36px rgba(80, 70, 30, 0.16), 0 3px 8px rgba(183, 125, 22, 0.12);

  position: relative;
}

.final-block:before {
  content: "";

  position: absolute;

  inset: 0;

  border-radius: inherit;

  background: radial-gradient(circle at 50% 0, rgba(212, 154, 42, 0.11), transparent 55%);

  pointer-events: none;
}

.final-label {
  display: flex;

  align-items: center;

  gap: 7px;

  color: #7a4f08;

  font-size: 11px;

  font-weight: 900;

  letter-spacing: 1px;

  text-transform: uppercase;

  position: relative;
}

.final-icon {
  width: 25px;

  height: 25px;

  display: flex;

  align-items: center;

  justify-content: center;

  border-radius: 8px;

  color: #a66d0c;

  background: #fff5d7;

  border: 1px solid rgba(183, 125, 22, 0.25);
}

.final-subtitle {
  font-size: 7px;

  font-weight: 900;

  color: #9b8661;

  letter-spacing: 1px;

  margin: 4px 0 8px;
}

.final-card-wrap {
  width: 100%;

  display: flex;

  justify-content: center;
}

.j3-block {
  display: flex;

  flex-direction: column;

  align-items: center;

  padding: 10px 12px 12px;

  border-radius: 15px;

  background: #ffffff;

  border: 1px solid rgba(2, 140, 115, 0.22);

  box-shadow: 0 8px 22px rgba(13, 68, 56, 0.1);
}

.j3-label {
  display: flex;

  align-items: center;

  gap: 6px;

  margin-bottom: 8px;

  color: #23695c;

  font-size: 8px;

  font-weight: 900;

  letter-spacing: 0.6px;
}

.j3-icon {
  width: 22px;

  height: 22px;

  display: flex;

  align-items: center;

  justify-content: center;

  border-radius: 7px;

  color: var(--teal);

  background: var(--teal-soft);

  border: 1px solid rgba(2, 140, 115, 0.2);
}

.round-navigation {
  flex: 0 0 auto;

  padding: 8px 20px 7px;

  background: rgba(255, 255, 255, 0.97);

  border-bottom: 1px solid #dce9e5;

  box-shadow: 0 3px 12px rgba(13, 68, 56, 0.055);

  position: relative;

  z-index: 8;
}

.round-navigation-head {
  display: flex;

  align-items: center;

  justify-content: space-between;

  gap: 12px;

  margin-bottom: 7px;
}

.round-navigation-title {
  display: flex;

  align-items: center;

  gap: 8px;

  min-width: 0;
}

.round-navigation-icon {
  width: 29px;

  height: 29px;

  flex: 0 0 29px;

  border-radius: 8px;

  display: flex;

  align-items: center;

  justify-content: center;

  color: var(--teal);

  background: var(--teal-soft);

  border: 1px solid #c8e0d9;
}

.round-navigation-title div {
  display: flex;

  flex-direction: column;

  min-width: 0;
}

.round-navigation-title strong {
  font-size: 8px;

  letter-spacing: 0.8px;
}

.round-navigation-title small {
  margin-top: 2px;

  color: #71847f;

  font-size: 7px;

  font-weight: 700;
}

.round-tabs {
  display: flex;

  align-items: stretch;

  gap: 5px;

  overflow-x: auto;

  scrollbar-width: thin;

  scrollbar-color: #b5d5cd transparent;

  padding-bottom: 1px;
}

.round-tab {
  flex: 0 0 auto;

  min-height: 31px;

  display: inline-flex;

  align-items: center;

  justify-content: center;

  gap: 7px;

  padding: 5px 11px;

  border: 1px solid #d4e4df;

  border-radius: 9px;

  background: #f8fbfa;

  color: #55736b;

  font-size: 8px;

  font-weight: 900;

  letter-spacing: 0.35px;

  cursor: pointer;

  transition: all 0.18s ease;

  white-space: nowrap;
}

.round-tab:hover {
  border-color: #9dc9bd;

  background: #eff8f5;

  transform: translateY(-1px);
}

.round-tab b {
  min-width: 17px;

  height: 17px;

  padding: 0 4px;

  display: inline-flex;

  align-items: center;

  justify-content: center;

  border-radius: 999px;

  background: #e6f2ee;

  color: #4e766b;

  font-size: 7px;
}

.round-tab.active {
  background: var(--teal);

  border-color: var(--teal);

  color: #fff;

  box-shadow: 0 4px 12px rgba(2, 140, 115, 0.2);
}

.round-tab.active b {
  background: rgba(255, 255, 255, 0.18);

  color: #fff;
}

.round-tab.all.active {
  background: #173f37;

  border-color: #173f37;
}

.round-search {
  width: 210px;

  flex: 0 0 210px;

  height: 31px;

  display: flex;

  align-items: center;

  gap: 6px;

  padding: 0 8px;

  border: 1px solid #d4e4df;

  border-radius: 9px;

  background: #fbfdfc;

  color: #6d827c;
}

.round-search:focus-within {
  border-color: #91c5b8;

  box-shadow: 0 0 0 3px rgba(2, 140, 115, 0.07);
}

.round-search input {
  width: 100%;

  min-width: 0;

  border: 0;

  outline: 0;

  background: transparent;

  color: var(--text);

  font-family: inherit;

  font-size: 8px;
}

.round-search input::placeholder {
  color: #8a9c97;
}

.clear-search {
  width: 19px;

  height: 19px;

  flex: 0 0 19px;

  padding: 0;

  border: 0;

  border-radius: 50%;

  background: #e9f1ef;

  color: #5b7770;

  cursor: pointer;
}

.search-result-banner {
  margin: 6px 20px 0;

  min-height: 30px;

  padding: 6px 9px;

  display: flex;

  align-items: center;

  gap: 7px;

  border: 1px solid #bfe0d6;

  border-radius: 8px;

  background: #eff9f6;

  color: #37675c;

  font-size: 8px;
}

.search-result-banner.empty {
  border-color: #ead7d1;

  background: #fff8f5;

  color: #8a655b;
}

.search-result-banner > span {
  min-width: 0;

  flex: 1;
}

.search-result-banner button {
  display: inline-flex;

  align-items: center;

  gap: 4px;

  border: 0;

  border-radius: 7px;

  padding: 5px 8px;

  background: var(--teal);

  color: #fff;

  font-size: 7px;

  font-weight: 900;

  cursor: pointer;
}

.round-view {
  flex: 1 1 auto;

  height: auto;

  min-height: 0;

  overflow: auto;

  padding: 12px 20px 26px;

  background: linear-gradient(rgba(241, 249, 247, 0.9), rgba(241, 249, 247, 0.9)),
    url("/images/bglivepes.png");

  background-size: cover;

  background-position: center;

  scrollbar-color: #9ccbc0 transparent;

  scrollbar-width: thin;
}

.round-view::-webkit-scrollbar {
  width: 10px;

  height: 10px;
}

.round-view::-webkit-scrollbar-thumb {
  background: #9ccbc0;

  border-radius: 999px;
}

.round-view-heading {
  display: flex;

  align-items: center;

  justify-content: space-between;

  gap: 15px;

  max-width: 1500px;

  margin: 0 auto 11px;

  padding: 7px 10px;

  border: 1px solid #d3e5df;

  border-radius: 12px;

  background: rgba(255, 255, 255, 0.94);
}

.round-view-kicker {
  display: block;

  color: #568077;

  font-size: 7px;

  font-weight: 900;

  letter-spacing: 1px;
}

.round-view-heading h2 {
  margin: 2px 0 0;

  color: #173f37;

  font-size: 17px;
}

.round-view-count {
  display: flex;

  align-items: baseline;

  gap: 5px;

  color: #6b817b;
}

.round-view-count strong {
  color: var(--teal);

  font-size: 21px;

  line-height: 1;
}

.round-view-count span {
  font-size: 7px;

  font-weight: 900;

  text-transform: uppercase;
}

.round-match-grid {
  max-width: 1500px;

  margin: 0 auto;

  display: grid;

  grid-template-columns: repeat(auto-fit, minmax(245px, 1fr));

  gap: 10px;

  align-items: start;
}

.round-match-item {
  position: relative;

  padding: 7px;

  border: 1px solid #d4e5e0;

  border-radius: 12px;

  background: rgba(255, 255, 255, 0.94);

  box-shadow: 0 5px 15px rgba(13, 68, 56, 0.06);

  transition: transform 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease;
}

.round-match-item:hover {
  transform: translateY(-1px);

  border-color: #a9cfc5;

  box-shadow: 0 8px 18px rgba(13, 68, 56, 0.09);
}

.round-match-item.search-highlight {
  border-color: var(--teal);

  box-shadow: 0 0 0 2px rgba(2, 140, 115, 0.12), 0 9px 22px rgba(13, 68, 56, 0.1);
}

.round-match-meta {
  display: flex;

  align-items: center;

  justify-content: space-between;

  gap: 8px;

  margin-bottom: 6px;

  padding: 0 2px;

  color: #6c827b;

  font-size: 7px;

  font-weight: 900;

  text-transform: uppercase;

  letter-spacing: 0.45px;
}

.round-match-meta b {
  color: var(--teal);
}

.round-match-schedule {
  margin-top: 5px;

  display: flex;

  align-items: center;

  justify-content: center;

  gap: 4px;

  color: #71847e;

  font-size: 7px;

  font-weight: 700;
}

.round-empty {
  min-height: 260px;

  max-width: 600px;

  margin: 30px auto;

  display: flex;

  flex-direction: column;

  align-items: center;

  justify-content: center;

  gap: 7px;

  border: 1px dashed #bfd8d1;

  border-radius: 16px;

  background: rgba(255, 255, 255, 0.72);

  color: #668079;

  text-align: center;
}

.round-empty strong {
  color: #315b52;

  font-size: 12px;
}

.round-empty span {
  font-size: 8px;
}

.bracket-hint {
  min-height: 24px;

  display: flex;

  align-items: center;

  justify-content: center;

  gap: 5px;

  padding: 3px 10px;

  color: #718780;

  background: rgba(255, 255, 255, 0.9);

  border-top: 1px solid #dce9e5;

  font-size: 7px;

  font-weight: 700;
}

.info-grid {
  flex: 0 0 126px;

  min-height: 126px;

  display: grid;

  grid-template-columns:
    1.05fr
    1.5fr
    1fr;

  gap: 10px;

  padding: 8px 20px;

  background: #ffffff;

  border-top: 1px solid #dfeae7;
}

.bracket-shell.round-mode + .info-grid {
  display: none;
}

.info-card {
  min-height: 0;

  overflow: hidden;

  border: 1px solid #dbe8e4;

  border-radius: 12px;

  padding: 10px 12px;

  background: #fbfdfc;

  min-width: 0;
}

.info-card-head {
  display: flex;

  align-items: center;

  gap: 6px;

  font-size: 12px;

  letter-spacing: 0.5px;

  margin-bottom: 8px;
}

.info-icon {
  width: 22px;

  height: 22px;

  border-radius: 7px;

  display: flex;

  align-items: center;

  justify-content: center;

  background: var(--teal-soft);

  color: var(--teal);
}

.info-list {
  display: grid;

  gap: 5px;
}

.info-list div {
  display: flex;

  justify-content: space-between;

  gap: 10px;

  font-size: 12px;

  color: #71827e;
}

.info-list b {
  color: #34564e;

  font-size: 12px;

  text-align: right;
}

.next-list {
  display: grid;

  gap: 5px;
}

.next-match {
  display: flex;

  align-items: center;

  justify-content: space-between;

  gap: 8px;

  padding: 5px 7px;

  border-radius: 7px;

  background: #f3f8f6;

  font-size: 7px;
}

.next-match div {
  display: flex;

  gap: 6px;

  align-items: center;

  min-width: 0;
}

.next-match b {
  max-width: 100px;

  overflow: hidden;

  text-overflow: ellipsis;

  white-space: nowrap;
}

.next-match time {
  font-weight: 900;

  color: var(--teal);

  white-space: nowrap;
}

.next-round {
  color: #6e827d;

  font-weight: 900;
}

.next-empty {
  font-size: 8px;

  color: #738580;

  display: flex;

  gap: 5px;

  align-items: center;

  padding-top: 7px;
}

.live-summary {
  display: flex;

  align-items: center;

  gap: 9px;
}

.summary-number {
  font-size: 32px;

  font-weight: 900;

  color: var(--teal);

  line-height: 1;
}

.live-summary b {
  display: block;

  font-size: 12px;
}

.live-summary span {
  display: block;

  font-size: 10px;

  color: #778b85;

  margin-top: 3px;
}

.copy-link {
  margin-top: 9px;

  border: 1px solid #cfe3dd;

  background: #ffffff;

  color: #2b6f62;

  border-radius: 7px;

  padding: 5px 8px;

  font-size: 7px;

  cursor: pointer;
}

.live-footer {
  height: 30px;

  flex: 0 0 30px;

  display: flex;

  align-items: center;

  justify-content: space-between;

  padding: 0 20px;

  background: #063f35;

  color: #d9ece7;

  font-size: 7px;

  position: relative;

  z-index: 5;
}

.live-footer span {
  display: flex;

  align-items: center;

  gap: 5px;
}

.live-empty {
  position: fixed;

  inset: 68px 0 0;

  display: flex;

  flex-direction: column;

  align-items: center;

  justify-content: center;

  background: #f7faf9;

  text-align: center;
}

.empty-icon {
  width: 72px;

  height: 72px;

  border-radius: 20px;

  background: var(--teal-soft);

  display: flex;

  align-items: center;

  justify-content: center;

  color: var(--teal);
}

.live-empty h2 {
  font-size: 18px;

  margin: 14px 0 5px;
}

.live-empty p {
  font-size: 10px;

  color: #71827d;
}

.login-gate {
  position: fixed;

  inset: 0;

  display: flex;

  align-items: center;

  justify-content: center;

  background: linear-gradient(135deg, #eef8f5, #f9fbfa);

  z-index: 100;
}

.login-gate-box {
  width: 320px;

  padding: 28px;

  border-radius: 18px;

  background: #ffffff;

  border: 1px solid #cde2dc;

  box-shadow: 0 18px 50px rgba(13, 68, 56, 0.13);

  display: flex;

  flex-direction: column;

  gap: 10px;

  text-align: center;
}

.login-gate-logo {
  width: 48px;

  height: 48px;

  object-fit: contain;

  margin: auto;
}

.login-game-badge {
  font-size: 8px;

  color: var(--teal);

  font-weight: 900;
}

.login-gate-box h2 {
  font-size: 17px;

  margin: 0;
}

.login-gate-box p {
  font-size: 10px;

  color: #71827d;

  margin: 0 0 5px;
}

.login-gate-box input {
  padding: 10px 12px;

  border: 1px solid #d5e4e0;

  border-radius: 9px;

  font-size: 12px;
}

.login-gate-box input:focus {
  outline: none;

  border-color: var(--teal);

  box-shadow: 0 0 0 3px rgba(2, 140, 115, 0.08);
}

.login-gate-box button {
  padding: 10px;

  border: 0;

  border-radius: 9px;

  background: var(--teal);

  color: #ffffff;

  font-weight: 900;

  cursor: pointer;

  transition: background 0.2s ease, transform 0.2s ease;
}

.login-gate-box button:hover {
  background: var(--teal-dark);

  transform: translateY(-1px);
}

.login-gate-error {
  font-size: 9px;

  color: #c43d3d;

  font-weight: 700;
}

@media (max-width: 900px) {
  .round-navigation-head {
    align-items: stretch;

    flex-direction: column;

    gap: 6px;
  }

  .round-search {
    width: 100%;

    flex-basis: 31px;
  }

  .round-match-grid {
    grid-template-columns: repeat(2, minmax(220px, 1fr));
  }
}

@media (max-width: 1100px) {
  .date-clock {
    display: none;
  }

  .brand-text {
    min-width: auto;
  }

  .game-divider {
    margin: 0 5px;
  }

  .info-grid {
    grid-template-columns: 1fr 1fr;
  }

  .quick-card {
    display: none;
  }
}

@media (max-width: 700px) {
  .live-topbar {
    height: 52px;
    padding: 0 8px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 6px;
    flex-wrap: nowrap;
  }

  .brand-area {
    gap: 7px;
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;
    flex-wrap: nowrap;
  }

  .live-logo {
    width: 32px;
    height: 32px;
    flex: 0 0 32px;
  }

  .brand-text {
    min-width: 0;
    overflow: hidden;
    flex: 1 1 auto;
  }

  .brand-text strong {
    font-size: 11.5px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    display: block;
    line-height: 1.2;
  }

  .brand-subtitle-desktop {
    display: none !important;
  }

  .brand-subtitle-mobile {
    display: block !important;
    font-size: 8px;
    font-weight: 800;
    color: rgba(255, 255, 255, 0.85);
    letter-spacing: 0.3px;
    margin-top: 1px;
    white-space: nowrap;
  }

  .game-divider,
  .game-icon,
  .game-title-wrap,
  .date-clock {
    display: none !important;
  }

  .header-actions {
    gap: 5px;
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    flex-wrap: nowrap;
  }

  .live-status {
    padding: 4px 7px;
    font-size: 8px;
    font-weight: 850;
    border-radius: 999px;
    gap: 4px;
    white-space: nowrap;
  }

  .status-full {
    display: none !important;
  }

  .status-short {
    display: inline !important;
  }

  .header-screenshot-btn {
    width: 32px !important;
    height: 32px !important;
    min-width: 32px !important;
    padding: 0 !important;
    justify-content: center !important;
    border-radius: 8px !important;
    display: inline-flex !important;
    align-items: center !important;
  }

  .screenshot-btn-text {
    display: none !important;
  }

  .icon-btn {
    width: 32px;
    height: 32px;
    min-width: 32px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .champion-banner {
    padding: 5px 8px;
    flex-basis: 44px;
    min-height: 44px;
    overflow-x: auto;
    overflow-y: hidden;
    -webkit-overflow-scrolling: touch;
    gap: 6px;
  }

  .champion-title {
    display: none;
  }

  .champion-item {
    height: 30px;
    padding: 3px 9px;
    flex-shrink: 0;
    font-size: 8.5px;
    gap: 5px;
  }

  .champion-item span {
    display: inline;
    font-size: 8px;
    color: #857048;
    margin-right: 1px;
  }

  .champion-item strong {
    max-width: 140px;
    font-size: 9px;
  }

  .live-content {
    flex: 1 1 auto;

    height: auto;

    min-height: 0;
  }

  .champion-banner {
    flex-basis: 43px;

    min-height: 43px;
  }

  .round-navigation {
    padding: 6px 8px;
  }

  .round-navigation-title strong {
    font-size: 7px;
  }

  .round-navigation-title small {
    font-size: 6px;
  }

  .round-tabs {
    gap: 4px;
  }

  .round-tab {
    min-height: 29px;

    padding: 4px 8px;

    font-size: 7px;
  }

  .round-view {
    padding: 8px 8px 14px;
  }

  .round-view-heading {
    margin-bottom: 8px;
  }

  .round-view-heading h2 {
    font-size: 14px;
  }

  .round-match-grid {
    grid-template-columns: 1fr;
  }

  .search-result-banner {
    margin-left: 8px;

    margin-right: 8px;
  }

  .live-toolbar {
    height: 55px;

    flex-basis: 55px;

    padding: 7px 10px;
  }

  .section-kicker,
  .legend,
  .refresh-control {
    display: none;
  }

  .toolbar-title h2 {
    font-size: 10px;
  }

  .share-btn {
    padding: 7px 9px;
  }

  .bracket-shell {
    padding: 0 5px;
  }

  .mode-kbd {
    display: none;
  }

  .hand-mode-hint {
    bottom: 14px;
    font-size: 9px;
    padding: 6px 11px;
  }

  .hand-mode-hint em {
    display: none;
  }

  .info-grid {
    display: none;
  }

  .live-footer {
    display: none;
  }
}

@media (max-width: 480px) {
  .live-topbar {
    height: 50px;
    padding: 0 6px;
  }

  .brand-area {
    gap: 6px;
  }

  .live-logo {
    width: 28px;
    height: 28px;
    flex: 0 0 28px;
  }

  .brand-text strong {
    font-size: 11px;
  }

  .brand-subtitle-mobile {
    font-size: 7.5px;
  }

  .live-status {
    padding: 3px 6px;
    font-size: 7.5px;
  }

  .header-actions {
    gap: 4px;
  }

  .header-screenshot-btn,
  .icon-btn {
    width: 29px !important;
    height: 29px !important;
    min-width: 29px !important;
    border-radius: 7px !important;
  }
}
</style>

<style>
.screenshot-preview-dialog {
  border-radius: 20px !important;
  overflow: hidden !important;
  box-shadow: 0 25px 60px rgba(15, 23, 42, 0.4) !important;
}

.screenshot-dialog-card {
  border-radius: 20px !important;
  overflow: hidden;
  background: #ffffff !important;
  display: flex;
  flex-direction: column;
}

.screenshot-dialog-header {
  padding: 16px 22px;
  background: #ffffff;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}

.screenshot-dialog-header .header-left {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.header-icon-box {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: rgba(2, 140, 115, 0.1);
  border: 1px solid rgba(2, 140, 115, 0.18);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.header-title-text {
  min-width: 0;
}

.screenshot-dialog-header h3 {
  margin: 0;
  font-size: 17px;
  font-weight: 850;
  color: #1e293b;
}

.screenshot-dialog-header p {
  margin: 3px 0 0;
  font-size: 12px;
  color: #64748b;
}

.dot-separator {
  margin: 0 6px;
  color: #cbd5e1;
}

.header-tools {
  display: flex;
  align-items: center;
  gap: 8px;
}

.zoom-pill-group {
  display: inline-flex;
  align-items: center;
  background: #f1f5f9;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  padding: 2px;
}

.zoom-btn {
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  border: none;
  background: transparent;
  color: #475569;
  cursor: pointer;
  transition: all 0.15s ease;
}

.zoom-btn:hover:not(:disabled) {
  background: #ffffff;
  color: #028c73;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
}

.zoom-btn:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

.zoom-value {
  padding: 0 10px;
  font-size: 11px;
  font-weight: 750;
  color: #334155;
  min-width: 48px;
  text-align: center;
  cursor: pointer;
  user-select: none;
}

.tool-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 11px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: #ffffff;
  color: #475569;
  font-size: 11.5px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.18s ease;
  height: 32px;
}

.tool-btn:hover,
.tool-btn.active {
  border-color: #028c73;
  color: #028c73;
  background: #e8f6f2;
}

.close-dialog-btn {
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: #ffffff;
  color: #64748b;
  cursor: pointer;
  transition: all 0.15s ease;
}

.close-dialog-btn:hover {
  background: #fee2e2;
  color: #ef4444;
  border-color: #fca5a5;
}

.mobile-close-btn {
  display: none;
}

.desktop-close-btn {
  display: inline-flex;
}

.screenshot-dialog-body {
  padding: 16px 20px !important;
  background: #0f172a !important;
}

.screenshot-canvas-viewport {
  width: 100%;
  max-height: 64vh;
  overflow: auto;
  border-radius: 12px;
  background: #1e293b;
  box-shadow: inset 0 2px 10px rgba(0, 0, 0, 0.4);
  padding: 20px;
  text-align: center;
  scrollbar-width: thin;
  scrollbar-color: #475569 #1e293b;
}

.screenshot-canvas-viewport::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.screenshot-canvas-viewport::-webkit-scrollbar-track {
  background: #1e293b;
}

.screenshot-canvas-viewport::-webkit-scrollbar-thumb {
  background: #475569;
  border-radius: 4px;
}

.screenshot-img-container {
  display: block;
  margin: 0 auto;
  transition: width 0.15s ease-out, min-width 0.15s ease-out;
}

.screenshot-result-image {
  width: 100%;
  height: auto;
  display: block;
  border-radius: 8px;
  box-shadow: 0 10px 35px rgba(0, 0, 0, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.viewport-hint {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 10px;
  font-size: 11px;
  color: #94a3b8;
}

.screenshot-dialog-footer {
  padding: 14px 22px !important;
  background: #ffffff !important;
  border-top: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
}

.footer-meta {
  display: flex;
  align-items: center;
  gap: 10px;
}

.file-name-preview {
  font-size: 12px;
  color: #64748b;
  font-family: monospace;
  background: #f1f5f9;
  padding: 4px 8px;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
}

.footer-buttons {
  display: flex;
  align-items: center;
  gap: 10px;
}

.footer-secondary-btns {
  display: flex;
  align-items: center;
  gap: 10px;
}

.cancel-btn {
  border-radius: 10px !important;
  text-transform: none !important;
  font-weight: 700 !important;
  color: #64748b !important;
  border: 1px solid #cbd5e1 !important;
  background: #ffffff !important;
}

.cancel-btn:hover {
  background: #f1f5f9 !important;
  color: #334155 !important;
}

.open-tab-btn {
  border-radius: 10px !important;
  text-transform: none !important;
  font-weight: 700 !important;
  color: #028c73 !important;
  border: 1px solid rgba(2, 140, 115, 0.35) !important;
  background: #ffffff !important;
}

.open-tab-btn:hover {
  background: #e8f6f2 !important;
}

.download-btn {
  border-radius: 10px !important;
  text-transform: none !important;
  font-weight: 750 !important;
  background: #028c73 !important;
  color: #ffffff !important;
  box-shadow: 0 4px 14px rgba(2, 140, 115, 0.3) !important;
}

.download-btn:hover {
  background: #016653 !important;
}

@media (max-width: 680px) {
  .screenshot-preview-dialog {
    margin: 6px !important;
    width: calc(100vw - 12px) !important;
    max-width: calc(100vw - 12px) !important;
    max-height: 96vh !important;
    border-radius: 16px !important;
  }

  .screenshot-dialog-card {
    height: 94vh !important;
    max-height: 94vh !important;
    border-radius: 16px !important;
    display: flex !important;
    flex-direction: column !important;
    overflow: hidden !important;
  }

  .screenshot-dialog-header {
    padding: 10px 12px !important;
    gap: 8px !important;
    flex-shrink: 0 !important;
    display: flex !important;
    flex-direction: column !important;
    align-items: stretch !important;
  }

  .screenshot-dialog-header .header-left {
    display: flex !important;
    align-items: center !important;
    justify-content: space-between !important;
    width: 100% !important;
    gap: 8px !important;
  }

  .header-icon-box {
    width: 32px !important;
    height: 32px !important;
    min-width: 32px !important;
    border-radius: 8px !important;
  }

  .screenshot-dialog-header h3 {
    font-size: 13px !important;
    line-height: 1.2 !important;
  }

  .screenshot-dialog-header p {
    font-size: 10px !important;
    margin-top: 1px !important;
  }

  .mobile-close-btn {
    display: inline-flex !important;
    width: 28px !important;
    height: 28px !important;
    min-width: 28px !important;
    margin-left: auto !important;
  }

  .desktop-close-btn {
    display: none !important;
  }

  .header-tools {
    width: 100% !important;
    display: flex !important;
    align-items: center !important;
    justify-content: space-between !important;
    gap: 4px !important;
  }

  .zoom-pill-group {
    padding: 1px !important;
  }

  .zoom-btn {
    width: 26px !important;
    height: 26px !important;
  }

  .zoom-value {
    font-size: 10px !important;
    min-width: 36px !important;
    padding: 0 3px !important;
  }

  .tool-btn {
    height: 28px !important;
    padding: 0 7px !important;
    font-size: 10px !important;
    gap: 3px !important;
  }

  .screenshot-dialog-body {
    flex: 1 1 auto !important;
    min-height: 0 !important;
    padding: 8px 10px !important;
    display: flex !important;
    flex-direction: column !important;
    overflow: hidden !important;
  }

  .screenshot-canvas-viewport {
    flex: 1 1 auto !important;
    min-height: 100px !important;
    max-height: none !important;
    height: 100% !important;
    padding: 8px !important;
    overflow: auto !important;
    -webkit-overflow-scrolling: touch;
    border-radius: 10px !important;
  }

  .viewport-hint {
    margin-top: 4px !important;
    font-size: 9.5px !important;
  }

  .screenshot-dialog-footer {
    flex-shrink: 0 !important;
    padding: 10px 12px 12px !important;
    gap: 8px !important;
    display: flex !important;
    flex-direction: column !important;
    align-items: stretch !important;
    background: #ffffff !important;
    border-top: 1px solid #e2e8f0 !important;
    box-shadow: 0 -3px 12px rgba(0, 0, 0, 0.05) !important;
  }

  .footer-meta {
    display: none !important;
  }

  .footer-buttons {
    width: 100% !important;
    display: flex !important;
    flex-direction: column !important;
    align-items: stretch !important;
    gap: 8px !important;
  }

  .footer-secondary-btns {
    width: 100% !important;
    display: flex !important;
    align-items: center !important;
    justify-content: space-between !important;
    gap: 8px !important;
  }

  .cancel-btn {
    flex: 1 1 50% !important;
    height: 38px !important;
    min-height: 38px !important;
    padding: 0 10px !important;
    font-size: 12px !important;
    font-weight: 700 !important;
    border-radius: 9px !important;
    border: 1px solid #cbd5e1 !important;
    background: #f8fafc !important;
    color: #475569 !important;
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
  }

  .open-tab-btn {
    flex: 1 1 50% !important;
    height: 38px !important;
    min-height: 38px !important;
    padding: 0 10px !important;
    font-size: 12px !important;
    font-weight: 700 !important;
    border-radius: 9px !important;
    border: 1px solid rgba(2, 140, 115, 0.4) !important;
    background: #f0fdf9 !important;
    color: #028c73 !important;
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
  }

  .download-btn {
    width: 100% !important;
    height: 42px !important;
    min-height: 42px !important;
    padding: 0 14px !important;
    font-size: 13px !important;
    font-weight: 800 !important;
    letter-spacing: 0.2px !important;
    border-radius: 9px !important;
    background: #028c73 !important;
    color: #ffffff !important;
    box-shadow: 0 3px 12px rgba(2, 140, 115, 0.3) !important;
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    white-space: nowrap !important;
  }

  .header-screenshot-btn {
    height: 30px;
    padding: 0 8px;
    font-size: 10px;
  }
}
</style>
